import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import {
  buildFloorPlanPaintSequence,
  createFloorPlanElementNode,
} from '../nodes/floor-plan-element-nodes';
import {
  floorPlanPerimeterChainPointsPx,
  groupPerimeterVerticesByPolygonId,
  sortedPolygonIds,
} from '../perimeter/floor-plan-perimeter-groups';
import { floorPlanVertexCenterCm } from '../perimeter/floor-plan-perimeter-vertex-geometry';
import {
  isFloorPlanElementTransformable,
  roundFloorPlanGeometryCm,
  syncKonvaNodeGeometryToElement,
} from '../geometry/floor-plan-konva-sync';
import {
  cmPointToStagePx,
  computeFloorPlanRoomRectStageAabb,
  computeUppFloorPlanViewTransform,
  stagePxToCmPoint,
  type UppFloorPlanViewTransform,
} from '../geometry/floor-plan-view-transform';
import {
  createUppFloorPlanRoomDimensionGroup,
  estimateRoomDimensionLabelOverflowPx,
} from '../geometry/floor-plan-room-dimensions-konva';
import { loadKonvaCore } from '../konva/load-konva-core';
import { applyLayerPixelRatioCap } from '../sync/stage-pixel-ratio';
import {
  runFloorPlanViewAttentionPulseLoop,
  runFloorPlanViewClickPulse,
} from '../view/floor-plan-view-click-pulse';
import {
  clampFloorPlanUserZoomFactor,
  FLOOR_PLAN_USER_ZOOM_MIN,
  panDeltaToKeepFocalStagePoint,
  stepFloorPlanUserZoom,
  wheelDeltaToZoomMultiplier,
  type UppKonvaTransform,
} from '../view/floor-plan-view-navigation';
import { syncStageSizeToHost } from '../sync/sync-stage-bounds';
import { uppKonvaRuntimeTrace } from '../../internal/upp-konva-runtime-trace';
import { floorPlanElementUuid } from '../internal/floor-plan-uuid';
import {
  alignFloorPlanElementsCentersOnX,
  alignFloorPlanElementsCentersOnY,
  distributeFloorPlanElementsEvenGapsAlongX,
  distributeFloorPlanElementsEvenGapsAlongY,
} from '../geometry/floor-plan-multi-selection-geometry';
import { konvaNodeIsUnderTransformer } from '../sync/floor-plan-konva-transformer-hit';
import {
  floorPlanMarqueeBoxFromOpposingCorners,
  floorPlanMarqueeBoxTooSmall,
  floorPlanMarqueeDragExceedsStartThreshold,
} from '../sync/floor-plan-marquee-stage-geometry';
import {
  floorPlanPerimeterPreviewShouldDraw,
  resolveFloorPlanPerimeterPreviewSourceVertex,
} from '../perimeter/floor-plan-perimeter-preview-model';
import { collectFloorPlanElementIdsIntersectingStageBox } from '../sync/floor-plan-stage-marquee-pick';
import {
  floorPlanEditModeClickDetail,
  floorPlanPointerEventClickDetail,
  floorPlanPreserveSelectionRedrawForVertexContinueTap,
  vertexContinueDrawShouldEmit,
  vertexContinueDrawWithinTapWindow,
} from '../perimeter/floor-plan-pointer-gesture-detail';
import {
  applyUniformBBoxGeometryCm,
  applyUniformTableGeometryCm,
  uniformSyncMultiUniformKindGroupTransform,
} from '../geometry/floor-plan-multi-table-uniform-sync';
import {
  floorPlanResolveTransformEndAnchor,
  floorPlanTransformCommitRotationSnapshot,
  floorPlanTransformerBoundBoxWithMinSize,
  floorPlanTransformerEventNamespace,
} from '../sync/floor-plan-transformer-stage-helpers';
import {
  floorPlanElementLabelInsetBBoxCm,
  floorPlanElementLabelRectCornersLocalPx,
} from '../geometry/floor-plan-element-label-inset-bbox';
import {
  floorPlanVertexLabelRectCornersContentPx,
  pickStageTopRightCornerAmongVertices,
} from '../geometry/floor-plan-element-label-screen-anchor';
import { floorPlanElementLabelBadgeBoxLayout } from '../geometry/floor-plan-element-label-layout';
import { toUppKonvaSceneElementRef, toUppKonvaVertexSceneRef } from '../internal/floor-plan-scene-ref-map';
import {
  UppFloorPlanBar,
  UppFloorPlanVertex,
  UppFloorPlanTable,
  type UppFloorPlanElement,
  type UppFloorPlanElementOptions,
} from '../plan/floor-plan-element';
import { floorPlanBarStoolIndexFromPointerTarget } from '../render/bar';
import type {
  UppKonvaSceneElementRef,
  UppKonvaSceneElementRefVertex,
} from '../internal/upp-konva-scene-ref';
import type { UppFloorPlanModel } from '../plan/floor-plan-model';
import {
  mergeUppFloorPlanPreferences,
  type UppFloorPlanPreferences,
} from '../plan/floor-plan-preferences';
import {
  floorPlanInteriorWallStrokeWidthPx,
  mergeUppFloorPlanTheme,
  resolveUppFloorPlanThemeLabelFontSize,
  UPPKONVA_LIGHTTHEME,
  type UppFloorPlanTheme,
} from '../plan/floor-plan-theme';
import type { UppFloorPlanExportPngOptions } from '../export/upp-floor-plan-export-png';

export type { UppFloorPlanExportPngOptions } from '../export/upp-floor-plan-export-png';
export type { UppKonvaTransform } from '../view/floor-plan-view-navigation';

/** Options for {@link UppFloorPlanStageComponent.selectOnly}. */
export interface UppFloorPlanSelectOnlyOptions {
  /**
   * When `false`, updates selection and emits {@link UppFloorPlanStageComponent.onSelect} without rebuilding the scene
   * (used at drag start so the first drag gesture can select and move in one action). Default `true`.
   */
  redraw?: boolean;
}

/** Payload for {@link UppFloorPlanStageComponent.elementClick} (template-friendly alternative to {@link UppFloorPlanStageComponent.onElementClick}). */
export interface UppFloorPlanElementClickPayload {
  readonly element: UppKonvaSceneElementRef;
  readonly domEvent: MouseEvent | PointerEvent;
  /** Same as {@link UppKonvaSceneElementRef.custom}; repeated for hosts that serialize events without keeping `element`. */
  readonly custom: any;
  /**
   * Solo si {@link UppKonvaSceneElementRef.kind} es **`bar`**: **`null`** = clic en el cuerpo de la barra;
   * number = global stool index. Not included for other element types.
   */
  readonly stool?: number | null;
}

/**
 * Emitted after drag or transform in {@link UppFloorPlanStageComponent.edit}.
 * The component repaints from the mutated model; the host may use this to persist or copy geometry into its own persistence layer.
 * Numeric fields follow {@link UPP_FLOOR_PLAN_GEOMETRY_CM_DECIMALS} (cm) and {@link UPP_FLOOR_PLAN_GEOMETRY_DEG_DECIMALS} (degrees).
 */
export interface UppFloorPlanElementGeometryPayload {
  readonly element: UppKonvaSceneElementRef;
  readonly xCm: number;
  readonly yCm: number;
  readonly widthCm: number;
  readonly heightCm: number;
  readonly rotationDeg: number;
  /** Same as {@link UppKonvaSceneElementRef.custom}; repeated for hosts that serialize events without keeping `element`. */
  readonly custom: any;
}

/** Payload for {@link UppFloorPlanStageComponent.roomBackgroundDoubleClick} and {@link UppFloorPlanStageComponent.roomBackgroundSingleClick}. */
export interface UppFloorPlanRoomBackgroundPointerPayload {
  readonly xCm: number;
  readonly yCm: number;
  readonly domEvent: MouseEvent | PointerEvent;
}

/**
 * Fired when the user completes a **double-click gesture** on a **vertex** (wall-chain marker) while {@link roomBackgroundPointerMode} is `doubleClickRoomCm` and {@link UppFloorPlanStageComponent.edit} is on
 * (detected via `click`/`tap`: `MouseEvent.detail >= 2` or two taps within ~480 ms — not Konva `dblclick`).
 * Hosts typically set {@link vertexPreviewAnchorId} to {@link UppFloorPlanVertexDoubleClickPayload.element}.`uuid` so the dashed preview and the next background double-clicks extend the chain from that vertex.
 */
export interface UppFloorPlanVertexDoubleClickPayload {
  readonly element: UppKonvaSceneElementRefVertex;
  readonly domEvent: MouseEvent | PointerEvent;
  readonly custom: any;
}

@Component({
  selector: 'upp-floor-plan-stage',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './upp-floor-plan.html',
  styleUrls: ['./upp-floor-plan.scss'],
})
export class UppFloorPlanStageComponent implements AfterViewInit, OnDestroy, OnChanges {
  /***********************************************/
  /* INPUTS                                      */
  /***********************************************/

  @Input() model: UppFloorPlanModel | null = null;
  /**
   * Contador del integrador de escena: sube en cada nuevo {@link UppFloorPlanModel} interno aunque el `@Input() model` del
   * host intermedio (`upp-konva-floor-plan`) sea la misma instancia; evita que OnPush omita el repintado tras `area = …`.
   */
  @Input() presentationEpoch = 0;
  /** Enables pointer interaction for editing gestures (F5). The host app must set this from its own permission/UI rules; this library does not check users or roles. */
  @Input() edit = false;
  /** When false, draw items do not listen to pointer events (read-only). The host app controls this; no auth logic lives in this package. */
  @Input() active = true;
  /**
   * View mode only (`edit === false`): duration of the click feedback scale pulse (ms), in three equal phases
   * (grow toward {@link viewClickPulsePeakScale}, shrink toward {@link viewClickPulseValleyScale}, return to 1).
   * Set to `0` to disable. Does not run in edit mode.
   */
  @Input() viewClickPulseDurationMs = 500;
  /** Peak scale during the pulse (e.g. `1.05` = +5%). View mode only. */
  @Input() viewClickPulsePeakScale = 1.05;
  /** Mid-pulse scale (e.g. `0.95` = −5% vs original). View mode only. */
  @Input() viewClickPulseValleyScale = 0.95;
  @Input() preferences: Partial<UppFloorPlanPreferences> | null = null;
  @Input() theme: Partial<UppFloorPlanTheme> | null = null;
  /**
   * When `doubleClickRoomCm`, **double-click / double-tap** on empty room (stage / layer / content, not on an element or Transformer)
   * emits {@link roomBackgroundDoubleClick} with room cm coordinates — for hosts that place **vertices** (wall-chain markers; demo).
   * A **double-click gesture** on a {@link UppFloorPlanVertex} (`click` with `detail >= 2` or two taps within ~650 ms) emits {@link vertexDoubleClick} so the host can anchor the next segment there (see {@link vertexPreviewAnchorId}) — including after the host has set **`closed`** on the polygon (host may reopen the chain to keep drawing).
   * A **debounced single click** on the same empty background emits {@link roomBackgroundSingleClick} (after ~450ms, cancelled if a double-click
   * completes first) so the host can close and fill a polygon.
   * Only applies with {@link UppFloorPlanStageComponent.edit} true. A **dashed line** previews the next edge from the anchor vertex (see {@link vertexPreviewAnchorId})
   * or, if unset, the open-chain vertex with the greatest `sort` (pointermove on the stage container; cleared on leave). Default `none`.
   */
  @Input() roomBackgroundPointerMode: 'none' | 'doubleClickRoomCm' = 'none';
  /**
   * When set, the wall-chain preview segment starts from this vertex **`uuid`** (room cm centre of the marker).
   * If unset, the preview starts from the greatest `sort` among vertices with `closed === false`.
   */
  @Input() vertexPreviewAnchorId: string | null = null;
  /**
   * When `true`, logs `console.info` lines (with `TODO`) for wall-chain “continue draw” detection — use only while debugging double-click on vertices.
   */
  @Input() debugVertexContinueDraw = false;

  /***********************************************/
  /* OUTPUTS                                     */
  /***********************************************/

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() readonly onSelect = new EventEmitter<readonly string[]>();
  /** Fired in Angular zone when the user clicks an element whose group listens (same targets as {@link onElementClick}). */
  @Output() readonly elementClick = new EventEmitter<UppFloorPlanElementClickPayload>();
  /** Fired in Angular zone after geometry commit and internal redraw (host optional: persist / sync). */
  @Output() readonly elementGeometryChange = new EventEmitter<UppFloorPlanElementGeometryPayload>();
  /**
   * View-mode navigation (`edit === false`): emitted after user zoom / pan (wheel, pinch, background drag, or imperative API).
   */
  @Output() readonly viewTransformChange = new EventEmitter<UppKonvaTransform>();
  /** Room cm position on double-click / double-tap over empty background when {@link roomBackgroundPointerMode} is `doubleClickRoomCm`. */
  @Output() readonly roomBackgroundDoubleClick = new EventEmitter<UppFloorPlanRoomBackgroundPointerPayload>();
  /**
   * Debounced single click on empty background (same mode as {@link roomBackgroundDoubleClick}); cancelled when a double-click is recognized.
   * Emitted only if the model has at least one {@link UppFloorPlanVertex} with `closed === false` (open chain), so plain background deselect does not fire when there is no draft polygon.
   */
  @Output() readonly roomBackgroundSingleClick = new EventEmitter<UppFloorPlanRoomBackgroundPointerPayload>();
  /**
   * Continue or resume the wall chain from a **vertex** when {@link roomBackgroundPointerMode} is `doubleClickRoomCm` and {@link UppFloorPlanStageComponent.edit} is on (open or closed chain; host decides whether to reopen).
   * Detected on `click`/`tap` via two clicks on the same vertex within ~650 ms (Konva's `evt` is usually `pointerup`, so `detail` is unreliable) or `detail >= 2` when the browser provides it.
   * {@link selectOnly} avoids a full redraw when the selection is unchanged so the Konva node survives between the two clicks.
   */
  @Output() readonly vertexDoubleClick = new EventEmitter<UppFloorPlanVertexDoubleClickPayload>();

  /***********************************************/
  /* VIEW                                        */
  /***********************************************/

  @ViewChild('frame', { read: ElementRef }) _frameRef!: ElementRef<HTMLDivElement>;

  /***********************************************/
  /* KONVA / OBSERVER                            */
  /***********************************************/

  private _konva: any = null;
  private _stage: any = null;
  private _layer: any = null;
  private _contentGroup: any = null;
  private _transformer: any = null;
  /** Model `rotationDeg` per selected transformable at `transformstart` (uniform multi-kind group). */
  private _uniformGroupTransformModelRotationSnapshot: readonly number[] | null = null;
  /**
   * `Transformer.getActiveAnchor()` at `transformstart` for uniform multi-select (e.g. `'rotater'`).
   * Do not rely on `getActiveAnchor()` at `transformend`: Konva clears `_movingAnchorName` after firing the event.
   */
  private _uniformGroupTransformGestureAnchor: string | null = null;
  /**
   * True when active handle was `'rotater'` in any transform of current gesture (uniform multi-selection).
   * Refuerza la rama `snapshot + delta` cuando `transformend` no conserva el nombre de ancla.
   */
  private _uniformGroupGestureUsedRotater = false;
  private _resizeObserver: ResizeObserver | null = null;
  private _destroyed = false;
  private _konvaReady = false;
  /** Stops the in-flight view-mode click pulse and restores node geometry. */
  private _viewClickPulseCancel: (() => void) | null = null;

  /** Stops looping attention pulses (view mode, tables with `options.attention`). */
  private readonly _attentionPulseCancelById = new Map<string, () => void>();

  private _userZoomFactor = 1;
  private _panOffsetXPx = 0;
  private _panOffsetYPx = 0;
  /**
   * Coalesces the {@link _redrawScene} + {@link _emitViewTransformChange} triggered by continuous zoom input
   * (wheel ticks, pinch `pointermove`) into at most one full scene rebuild per animation frame, instead of
   * one per raw event — see {@link _scheduleUserZoomRedraw}.
   */
  private _userZoomRedrawRafId = 0;
  private _viewNavCleanup: (() => void) | null = null;
  private _orientationLockUpright = false;
  private readonly _selectionIds = new Set<string>();
  private readonly _elementClickHandlers = new Set<
    (element: UppKonvaSceneElementRef, ev: MouseEvent | PointerEvent) => void
  >();

  /** Room cm under pointer while drawing perimeter (double-click mode); dashed preview segment only. */
  private _perimeterPreviewEndCm: { xCm: number; yCm: number } | null = null;
  private _perimeterPreviewRafId = 0;
  private _perimeterPreviewPointerCleanup: (() => void) | null = null;
  /** Cleared on destroy and when a double-click is handled so it does not fire after a vertex add. */
  private _roomBackgroundSingleClickTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Touch / synthetic clicks without `MouseEvent.detail >= 2`: two taps on the same perimeter vertex within this window
   * emit {@link vertexDoubleClick}. Mouse double-click uses `detail >= 2` (works with `draggable`; Konva `dblclick` often does not).
   */
  private _vertexContinueDrawLastAt = 0;
  private _vertexContinueDrawLastId = '';
  private readonly _vertexContinueDrawTapWindowMs = 650;

  /**
   * Selection ids right before last **single-item** `selectOnly` in edit mode (click with `detail === 1`);
   * used for **double click** (`detail >= 2`): union with clicked element without losing previous selection.
   */
  private _selectionIdsBeforeLastSingleEditClick: string[] | null = null;

  /**
   * After **Shift+pointerdown** that adds an id to selection: the following **click** must not treat it as
   * toggle (quitar) el mismo id.
   */
  private _lastShiftPointerDownMergeUuid: string | null = null;

  /** Marquee rectangle in `Stage` coordinates (sibling of `_contentGroup`). */
  private _marqueeOverlay: any = null;
  private _marqueeRect: any = null;
  private _marqueeCandidateStart: { x: number; y: number } | null = null;
  private _marqueeCandidatePointerId: number | null = null;
  private _marqueePointerCleanup: (() => void) | null = null;
  /**
   * Ignore next background `click`/`tap` (stage/layer/content) to avoid clearing selection.
   * Tras marquesina; y tras `transformend` → repintado que destruye el `Transformer` antes del `click`
   * del mismo gesto (Konva re-hittea y el objetivo pasa a ser fondo).
   */
  private _suppressNextBackgroundClick = false;
  /**
   * Host called {@link selectOnly} with ids not yet present in {@link UppFloorPlanStageComponent.model} (e.g. same turn where
   * binding updates before child `ngOnChanges`). Do not emit empty `onSelect`: it will re-emit when
   * the model that resolves those ids arrives (see `ngOnChanges` -> `model`).
   */
  private _selectionEmitDeferred = false;
  /**
   * Se incrementa al inicio de cada {@link _redrawScene} completo. Sirve para que el `requestAnimationFrame` de
   * `selectOnly(..., { redraw: false })` should **not** destroy a newly mounted `Transformer` if host already repainted
   * tras `onSelect` (parpadeo: anclas un frame y luego solo el borde rojo).
   */
  private _sceneRedrawGeneration = 0;
  /**
   * >0 mientras corre un {@link _redrawScene} (incl. anidados). Al destruir un `Konva.Transformer`, Konva puede
   * disparar `transformend` → `commitTransform` con referencias de la **pasada** de pintado; si se emitiera
   * {@link elementGeometryChange}, el host emite `(onChange)` y {@link UppKonvaArea.applyAdapterSnapshotFromStage}
   * can flush a stale wire (e.g. square table) over public area **after** a shape change.
   */
  private _redrawSceneDepth = 0;
  /**
   * Direct drag in edit mode (vertex/furniture): avoids full {@link _redrawScene} triggered by
   * English comment normalized.
   * when selecting another vertex in the same mousedown+drag gesture).
   */
  private _liveEditDragDepth = 0;
  /**
   * Parent (`_contentGroup`) x/y at multi-drag start, per element id. Same delta as the dragged node keeps rigid motion in room space.
   * Konva.Transformer also registers `dragmove.tr-konva*` and would apply an extra delta on siblings — detached when `targets.length > 1`.
   */
  private _multiDragStartParentPositions: Map<string, { x: number; y: number }> | null = null;
  /** {@link UppFloorPlanElement.xCm} / {@link UppFloorPlanElement.yCm} at multi-drag start (same delta in cm as the primary’s Konva move). */
  private _multiDragStartModelCm: Map<string, { xCm: number; yCm: number }> | null = null;
  private _multiDragPrimaryId: string | null = null;
  private _multiDragPrimaryParentStart: { x: number; y: number } | null = null;

  constructor(
    private readonly _elementRef: ElementRef<HTMLElement>,
    private readonly _ngZone: NgZone,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model'] && this._selectionEmitDeferred && this._selectionIds.size > 0) {
      const sel = this._liveElements().filter((e) => this._selectionIds.has(e.uuid));
      if (sel.length > 0) {
        this._emitSelection();
      }
    }
    /**
     * `presentationEpoch` only: {@link UppKonvaFloorPlanModel} already called `refreshFromModel(snapshot)` with
     * `UppFloorPlanModel` nuevo; el `@Input() model` del stage puede seguir siendo la referencia vieja hasta
     * the right data. Before Angular finishes CD, `_redrawScene()` here would repaint stale data over the correct canvas.
     */
    const presentationEpochOnly =
      !!changes['presentationEpoch'] &&
      !changes['model'] &&
      !changes['preferences'] &&
      !changes['theme'] &&
      !changes['active'] &&
      !changes['edit'] &&
      !changes['roomBackgroundPointerMode'] &&
      !changes['vertexPreviewAnchorId'];
    const willRedrawFromNgOnChanges =
      this._konvaReady &&
      !presentationEpochOnly &&
      (changes['model'] ||
        changes['presentationEpoch'] ||
        changes['preferences'] ||
        changes['theme'] ||
        changes['active'] ||
        changes['edit'] ||
        changes['roomBackgroundPointerMode'] ||
        changes['vertexPreviewAnchorId']);
    uppKonvaRuntimeTrace('UppFloorPlanStage.ngOnChanges', {
      changeKeys: Object.keys(changes),
      presentationEpoch: this.presentationEpoch,
      presentationEpochOnly,
      konvaReady: this._konvaReady,
      willRedrawFromNgOnChanges,
      modelElements: this.model?.elements?.length ?? 0,
    });
    if (willRedrawFromNgOnChanges) {
      this._ngZone.runOutsideAngular(() => this._redrawScene());
    }
  }

  ngAfterViewInit(): void {
    this._ngZone.runOutsideAngular(() => {
      void this._loadKonvaAndCreateStage();
    });
  }

  ngOnDestroy(): void {
    this._destroyed = true;
    this._teardownViewNavigation();
    this._cancelViewClickPulse();
    this._cancelAllAttentionPulses();
    if (this._perimeterPreviewRafId !== 0) {
      cancelAnimationFrame(this._perimeterPreviewRafId);
      this._perimeterPreviewRafId = 0;
    }
    if (this._userZoomRedrawRafId !== 0) {
      cancelAnimationFrame(this._userZoomRedrawRafId);
      this._userZoomRedrawRafId = 0;
    }
    this._perimeterPreviewPointerCleanup?.();
    this._perimeterPreviewPointerCleanup = null;
    this._cancelMarqueeDocListeners();
    this._clearMarqueeVisual();
    if (this._roomBackgroundSingleClickTimer) {
      clearTimeout(this._roomBackgroundSingleClickTimer);
      this._roomBackgroundSingleClickTimer = null;
    }
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._liveEditDragDepth = 0;
    this._destroyKonvaTransformersOnLayer();
    this._stage?.destroy();
    this._stage = null;
    this._konva = null;
    this._layer = null;
    this._contentGroup = null;
  }

  /***********************************************/
  /* PUBLIC IMPERATIVE API                       */
  /***********************************************/

  /**
   * Snapshot of each scene row (identity, geometry cm, labels, integrator `custom`).
   * For live mutation from the host, use {@link UppKonvaModel} / {@link UppKonvaShapeHandle}; the internal element classes are not part of the public API.
   */
  elements(): ReadonlyArray<UppKonvaSceneElementRef> {
    return this._liveElements().map(toUppKonvaSceneElementRef);
  }

  forEachElement(fn: (e: UppKonvaSceneElementRef) => void): void {
    for (const e of this._liveElements()) {
      fn(toUppKonvaSceneElementRef(e));
    }
  }

  getElementById(id: string): UppKonvaSceneElementRef | null {
    const el = this._getLiveElementById(id);
    return el ? toUppKonvaSceneElementRef(el) : null;
  }

  /**
   * Sets the primary label on the model element (drawn to the **right** of the shape when non-empty, with optional smaller `sublabel`).
   * Prefer {@link UppKonvaShapeHandle.label} when the host holds shape handles from {@link UppKonvaModel}.
   */
  setElementLabel(elementId: string, text: string): void {
    this.setElementOptions(elementId, { label: text });
  }

  /** Clears the primary label on the model element. */
  clearElementLabel(elementId: string): void {
    this.setElementLabel(elementId, '');
  }

  /**
   * Patches presentation options on a model element (`label`, `sublabel`, `custom`, `attention`, tono/pulso de mesa).
   * Pass `{ custom: null }` to clear the integrator payload.
   */
  setElementOptions(elementId: string, patch: Partial<UppFloorPlanElementOptions>): void {
    const el = this._getLiveElementById(elementId);
    if (!el) {
      return;
    }
    if (patch.label !== undefined) {
      el.label = patch.label;
    }
    if (patch.sublabel !== undefined) {
      el.sublabel = patch.sublabel;
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'custom')) {
      el.custom = patch.custom as any;
    }
    if (patch.attention !== undefined) {
      el.attention = patch.attention;
    }
    if (patch.attentionTone !== undefined) {
      el.attentionTone = patch.attentionTone;
    }
    if (patch.attentionAnimated !== undefined) {
      el.attentionAnimated = patch.attentionAnimated;
    }
    this._ngZone.runOutsideAngular(() => this._redrawScene());
  }

  /**
   * Subscribe to clicks on elements with `listening: true`.
   * @returns Unsubscribe function.
   */
  onElementClick(
    callback: (element: UppKonvaSceneElementRef, ev: MouseEvent | PointerEvent) => void,
  ): () => void {
    this._elementClickHandlers.add(callback);
    return () => {
      this._elementClickHandlers.delete(callback);
    };
  }

  clearSelection(): void {
    this._selectionEmitDeferred = false;
    this._selectionIds.clear();
    this._selectionIdsBeforeLastSingleEditClick = null;
    this._lastShiftPointerDownMergeUuid = null;
    this._emitSelection();
    this._ngZone.runOutsideAngular(() => this._redrawScene());
  }

  getSelection(): ReadonlyArray<UppKonvaSceneElementRef> {
    const ids = this._selectionIds;
    return this._liveElements()
      .filter((e) => ids.has(e.uuid))
      .map(toUppKonvaSceneElementRef);
  }

  /**
   * Replaces the selection with the given element ids (empty array clears the selection).
   * @param options.redraw Pass `false` to update selection without rebuilding the scene (advanced; default repaints).
   * When the id set is **unchanged** and redraw is requested, skips a full scene rebuild (only `Stage.batchDraw`) so the same Konva node instance survives — needed for double-click gestures on vertices after the first click already selected that vertex.
   * {@link UppFloorPlanStageComponent.onSelect} is emitted on every call (including when the id set is unchanged), so hosts can react to repeated taps on the same element.
   */
  selectOnly(ids: string[], options?: UppFloorPlanSelectOnlyOptions): void {
    if (ids.length !== 1) {
      this._selectionIdsBeforeLastSingleEditClick = null;
      this._lastShiftPointerDownMergeUuid = null;
    }
    if (ids.length === 0) {
      this._selectionEmitDeferred = false;
    }
    const normalized = ids.map((raw) => this._resolveStageElementUuidRef(raw));
    const incoming = new Set(normalized);
    const selectionUnchanged =
      incoming.size === this._selectionIds.size && [...incoming].every((id) => this._selectionIds.has(id));

    /** Before mutating selection/emitting: if something repaints synchronously, later rAF must not clear the new Transformer. */
    const sceneGenAtSelectOnlyEntry = this._sceneRedrawGeneration;

    this._selectionIds.clear();
    for (const id of normalized) {
      this._selectionIds.add(id);
    }
    this._emitSelection();
    const redraw = options?.redraw !== false;
    if (redraw) {
      if (!selectionUnchanged) {
        this._ngZone.runOutsideAngular(() => this._redrawScene());
      } else {
        this._ngZone.runOutsideAngular(() => {
          /** Tras `pointerdown` + `selectOnly(..., { redraw: false })` hace falta un repintado completo para el Transformer. */
          if (this.edit && this._selectionIds.size > 0 && this._transformer == null) {
            this._redrawScene();
          } else {
            this._stage?.batchDraw();
          }
        });
      }
    } else {
      /**
       * Diferido: no destruir Transformers en el mismo tick que el `pointerdown` (rompe el drag). Si entre medias
       * hubo un `_redrawScene` (p. ej. tras `onSelect` → `refreshFromModel` / `ngOnChanges`), **no** llamar a
       * `_destroyKonvaTransformersOnLayer`: it would remove the newly created frame.
       */
      this._ngZone.runOutsideAngular(() => {
        requestAnimationFrame(() => {
          if (this._destroyed) {
            return;
          }
          if (this._sceneRedrawGeneration !== sceneGenAtSelectOnlyEntry) {
            this._stage?.batchDraw();
            return;
          }
          this._destroyKonvaTransformersOnLayer();
          this._stage?.batchDraw();
        });
      });
    }
  }

  /** Resets user zoom and pan, then refits the base scale to the host frame. */
  fitRoomToFrame(): void {
    this._userZoomFactor = 1;
    this._panOffsetXPx = 0;
    this._panOffsetYPx = 0;
    this._ngZone.runOutsideAngular(() => this._redrawScene());
    this._emitViewTransformChange();
  }

  /** Current multiplicative user zoom (1 = fit-to-frame). */
  getUserZoomFactor(): number {
    return this._userZoomFactor;
  }

  zoomIn(): void {
    this._applyUserZoom(stepFloorPlanUserZoom(this._userZoomFactor, 'in'), null);
  }

  zoomOut(): void {
    this._applyUserZoom(stepFloorPlanUserZoom(this._userZoomFactor, 'out'), null);
  }

  /**
   * When `lock` is true, forces **0°** screen orientation and disables {@link UppFloorPlanPreferences.autoOptimizeOrientation}
   * for that draw (manual {@link UppFloorPlanPreferences.viewOrientationDeg} is ignored while locked).
   */
  setOrientationLockUpright(lock: boolean): void {
    this._orientationLockUpright = lock;
    this._ngZone.runOutsideAngular(() => this._redrawScene());
  }

  /** Clears upright lock so preferences (manual orientation or auto-optimize) apply again. */
  optimizeLayoutForScreen(): void {
    this._orientationLockUpright = false;
    this._ngZone.runOutsideAngular(() => this._redrawScene());
  }

  /**
   * Maps a client pointer event to room coordinates (cm), using the same fit transform as the current scene.
   * Returns `null` if there is no model or the host cannot be measured.
   */
  clientEventToRoomCm(ev: MouseEvent | PointerEvent): { xCm: number; yCm: number } | null {
    const model = this.model;
    if (!model) {
      return null;
    }
    const host = this._elementRef.nativeElement;
    const rect = host.getBoundingClientRect();
    const xPx = ev.clientX - rect.left;
    const yPx = ev.clientY - rect.top;
    const t = this._viewTransformWithPan(model);
    return stagePxToCmPoint(t, xPx, yPx);
  }

  /**
   * Con al menos dos seleccionados: `true` si todos comparten el mismo {@link UppKonvaSceneElementRef.kind}
   * (p. ej. solo mesas). Con 0–1 elemento, `true` (no hay mezcla).
   */
  selectionIsUniformKind(): boolean {
    const sel = this.getSelection();
    if (sel.length < 2) {
      return true;
    }
    const k0 = sel[0]?.kind;
    if (!k0) {
      return true;
    }
    return sel.every((e) => e.kind === k0);
  }

  /**
   * `true` si hay **≥ 2** elementos seleccionados, **todos del mismo** {@link UppKonvaSceneElementRef.kind}, y son
   * English comment normalized.
   * English comment normalized.
   */
  canGroupResizeSelection(): boolean {
    const sel = this.getSelection();
    if (sel.length < 2 || !this.selectionIsUniformKind()) {
      return false;
    }
    return sel.every((e) => e.kind !== 'vertex');
  }

  /**
   * Aligns the **geometric centers** of selected elements' logical rectangles
   * (sin tener en cuenta sillas, etiquetas u otros adornos del nodo Konva).
   *
   * - `vertical`: iguala el centro **X** (misma recta vertical en pantalla).
   * - `horizontal`: iguala el centro **Y** (misma recta horizontal en pantalla).
   *
   * Para cada elemento, la nueva esquina superior izquierda se calcula como
   * `target_center - size / 2`, preserving width, height and rotation.
   */
  alignSelectedElements(alignment: 'vertical' | 'horizontal'): void {
    const sel = [...this._getLiveSelection()];
    if (sel.length < 2 || !this.selectionIsUniformKind()) {
      return;
    }
    const model = this.model;
    if (!model) {
      return;
    }
    const t = this._computeViewTransformForModel(model);
    if (alignment === 'vertical') {
      alignFloorPlanElementsCentersOnX(t, sel);
    } else {
      alignFloorPlanElementsCentersOnY(t, sel);
    }
    for (const el of sel) {
      this._emitGeometryCommit(el);
    }
    this._ngZone.runOutsideAngular(() => this._redrawScene());
  }

  /**
   * Reparto equidistante entre **bordes en pantalla** (Stage): `vertical` = mismos huecos en **vertical** (`sy`);
   * `horizontal` = mismos huecos en **horizontal** (`sx`). Usa el envolvente del grupo y el bbox proyectado
   * (incluye `rotationDeg` del elemento).
   */
  distributeSelectedElements(axis: 'vertical' | 'horizontal'): void {
    const sel = [...this._getLiveSelection()];
    if (sel.length < 2 || !this.selectionIsUniformKind()) {
      return;
    }
    const model = this.model;
    if (!model) {
      return;
    }
    const t = this._computeViewTransformForModel(model);
    if (axis === 'vertical') {
      distributeFloorPlanElementsEvenGapsAlongY(t, sel);
    } else {
      distributeFloorPlanElementsEvenGapsAlongX(t, sel);
    }
    for (const el of sel) {
      this._emitGeometryCommit(el);
    }
    this._ngZone.runOutsideAngular(() => this._redrawScene());
  }

  /**
   * English comment normalized.
   * `kind` (mesas, barras, muebles o arquitectura), conservando el centro visual de cada uno. Emite
   * {@link elementGeometryChange} por elemento y repinta.
   *
   * English comment normalized.
   */
  applyUniformGeometryToSelection(widthCm: number, heightCm: number, rotationDeg: number): boolean {
    const sel = this._getLiveSelection().filter((e) => isFloorPlanElementTransformable(e));
    if (sel.length === 0 || !this.selectionIsUniformKind()) {
      return false;
    }
    if (
      !Number.isFinite(widthCm) ||
      !Number.isFinite(heightCm) ||
      !Number.isFinite(rotationDeg) ||
      widthCm <= 0 ||
      heightCm <= 0
    ) {
      return false;
    }
    if (sel.every((e) => e instanceof UppFloorPlanTable)) {
      applyUniformTableGeometryCm(sel as UppFloorPlanTable[], widthCm, heightCm, rotationDeg);
    } else {
      if (sel.some((e) => e instanceof UppFloorPlanTable)) {
        return false;
      }
      applyUniformBBoxGeometryCm(sel, widthCm, heightCm, rotationDeg);
    }
    for (const el of sel) {
      this._emitGeometryCommit(el);
    }
    this._ngZone.runOutsideAngular(() => this._redrawScene());
    return true;
  }

  /**
   * Alias de {@link applyUniformGeometryToSelection} (misma firma); conservado por compatibilidad con integradores
   * que solo usaban mesas.
   */
  applyUniformGeometryToSelectedTables(widthCm: number, heightCm: number, rotationDeg: number): boolean {
    return this.applyUniformGeometryToSelection(widthCm, heightCm, rotationDeg);
  }

  /**
   * Rebuilds the Konva scene from the current `@Input() model`, or from **`snapshot`** when el host aporta el
   * {@link UppFloorPlanModel} vivo del integrador (p. ej. tras `area = …`: el input del stage puede no haberse
   * already updated under OnPush).
   *
   * @param snapshot Opcional; si se omite o es `null`, se usa {@link model}.
   */
  refreshFromModel(snapshot?: UppFloorPlanModel | null): void {
    uppKonvaRuntimeTrace('UppFloorPlanStage.refreshFromModel', {
      hasSnapshot: snapshot != null,
      snapshotTableShapes:
        snapshot?.elements
          ?.filter((e): e is UppFloorPlanTable => e instanceof UppFloorPlanTable)
          .map((e) => ({ uuid: e.uuid, tableShape: e.tableShape })) ?? [],
    });
    this._ngZone.runOutsideAngular(() => {
      this._redrawScene(snapshot != null ? snapshot : undefined);
    });
  }

  /**
   * Renders the current Konva stage to a raster blob (PNG by default). Returns `null` if the stage is not ready.
   */
  async exportToPng(options?: UppFloorPlanExportPngOptions): Promise<Blob | null> {
    if (!this._stage || !this._konvaReady) {
      return null;
    }
    const blob = await this._stage.toBlob({
      mimeType: options?.mimeType ?? 'image/png',
      pixelRatio: options?.pixelRatio ?? 1,
      quality: options?.quality,
    });
    return blob ?? null;
  }

  /***********************************************/
  /* PRIVATE                                     */
  /***********************************************/

  /**
   * Resolves host/legacy ids to the element's canonical uuid
   * (exact match first, then deterministic v4 from non-v4 strings used as constructor seeds).
   */
  private _resolveStageElementUuidRef(raw: string): string {
    const t = raw.trim();
    const byExact = this._liveElements().find((e) => e.uuid === t);
    if (byExact) {
      return byExact.uuid;
    }
    const seeded = floorPlanElementUuid(t);
    const bySeed = this._liveElements().find((e) => e.uuid === seeded);
    return bySeed?.uuid ?? seeded;
  }

  private _liveElements(): readonly UppFloorPlanElement[] {
    return this.model?.elements ?? [];
  }

  private _getLiveElementById(id: string): UppFloorPlanElement | null {
    const t = id.trim();
    return (
      this._liveElements().find((e) => e.uuid === t) ??
      this._liveElements().find((e) => e.uuid === floorPlanElementUuid(t)) ??
      null
    );
  }

  /** Root Konva node of element in `_contentGroup` (`id()` or `attrs.id` for Konva compatibility). */
  private _findContentNodeByElementUuid(elementUuid: string): any {
    const g = this._contentGroup;
    if (!g || typeof g.findOne !== 'function') {
      return null;
    }
    const byIdFn = g.findOne(
      (n: any) => n !== undefined && typeof n.id === 'function' && n.id() === elementUuid,
    );
    if (byIdFn) {
      return byIdFn;
    }
    return g.findOne((n: any) => n !== undefined && n?.attrs?.id === elementUuid) ?? null;
  }

  /** Current selection as live model rows (internal geometry helpers need class instances). */
  private _getLiveSelection(): UppFloorPlanElement[] {
    const ids = this._selectionIds;
    return this._liveElements().filter((e) => ids.has(e.uuid));
  }

  private _emitSelection(): void {
    const ids = this._selectionIds;
    const sel = this._liveElements().filter((e) => ids.has(e.uuid));
    if (ids.size > 0 && sel.length === 0) {
      this._selectionEmitDeferred = true;
      return;
    }
    this._selectionEmitDeferred = false;
    this._ngZone.run(() => {
      this.onSelect.emit(sel.map((e) => e.uuid));
    });
  }

  private _emitGeometryCommit(el: UppFloorPlanElement): void {
    if (this._redrawSceneDepth > 0) {
      return;
    }
    this._ngZone.run(() => {
      this.elementGeometryChange.emit({
        element: toUppKonvaSceneElementRef(el),
        custom: el.custom,
        xCm: el.xCm,
        yCm: el.yCm,
        widthCm: el.widthCm,
        heightCm: el.heightCm,
        rotationDeg: el.rotationDeg,
      });
    });
  }

  private _clearVertexContinueDrawAccum(): void {
    this._vertexContinueDrawLastAt = 0;
    this._vertexContinueDrawLastId = '';
  }

  /**
   * Continue wall chain from vertex: second click of a mouse double-click (`detail >= 2`) or two taps on the
   * same vertex within {@link _vertexContinueDrawTapWindowMs}. Konva `dblclick` is not used — it
   * conflicts with `draggable`.
   *
   * @returns `true` when gesture is treated as **continue stroke** (double click / double tap) and
   * double-click multi-selection logic must not be applied.
   */
  private _maybeEmitVertexContinueDrawFromVertexClick(
    el: UppFloorPlanVertex,
    domEv: MouseEvent | PointerEvent,
  ): boolean {
    /** Konva passes the underlying pointerup/mouseup as `evt`; `detail` is unreliable (often 0). Prefer timing. */
    const detail = floorPlanPointerEventClickDetail(domEv);
    const emit = (): void => {
      if (this._roomBackgroundSingleClickTimer) {
        clearTimeout(this._roomBackgroundSingleClickTimer);
        this._roomBackgroundSingleClickTimer = null;
      }
      this._clearVertexContinueDrawAccum();
      if (this.debugVertexContinueDraw) {
        console.info('TODO: upp-floor-plan vertexDoubleClick emit', el.uuid, detail); // TODO
      }
      this._ngZone.run(() => {
        this.vertexDoubleClick.emit({
          element: toUppKonvaVertexSceneRef(el),
          domEvent: domEv,
          custom: el.custom,
        });
      });
    };
    const now = Date.now();
    const withinTapWindow = vertexContinueDrawWithinTapWindow(
      el.uuid,
      now,
      this._vertexContinueDrawLastId,
      this._vertexContinueDrawLastAt,
      this._vertexContinueDrawTapWindowMs,
    );
    if (this.debugVertexContinueDraw) {
      console.info('TODO: upp-floor-plan vertex continue draw click', {
        id: el.uuid,
        detail,
        lastId: this._vertexContinueDrawLastId,
        deltaMs: this._vertexContinueDrawLastAt ? now - this._vertexContinueDrawLastAt : null,
        withinTapWindow,
      }); // TODO
    }
    if (vertexContinueDrawShouldEmit(detail, withinTapWindow)) {
      emit();
      return true;
    }
    this._vertexContinueDrawLastAt = now;
    this._vertexContinueDrawLastId = el.uuid;
    return false;
  }

  /**
   * In {@link UppFloorPlanStageComponent.edit}: **single click** (`detail === 1`) replaces selection with the element; **double click**
   * (`detail >= 2`) unions the element with selection state from before the first click in the pair.
   */
  private _applyEditModeClickSelection(el: UppFloorPlanElement, domEv: MouseEvent | PointerEvent): void {
    const detail = floorPlanEditModeClickDetail(domEv);
    if (detail >= 2 && this._selectionIdsBeforeLastSingleEditClick !== null) {
      const merged = new Set([...this._selectionIdsBeforeLastSingleEditClick, el.uuid]);
      this.selectOnly([...merged]);
      this._selectionIdsBeforeLastSingleEditClick = null;
      return;
    }
    const shiftKey = (domEv as MouseEvent).shiftKey === true;
    if (shiftKey && detail === 1) {
      if (this._lastShiftPointerDownMergeUuid === el.uuid) {
        this._lastShiftPointerDownMergeUuid = null;
        /**
         * English comment normalized.
         * Without full repaint here, nodes keep previous-state stroke and only one id appears red.
         */
        this._ngZone.runOutsideAngular(() => this._redrawScene());
        return;
      }
      this._lastShiftPointerDownMergeUuid = null;
      const next = new Set(this._selectionIds);
      if (next.has(el.uuid)) {
        next.delete(el.uuid);
      } else {
        next.add(el.uuid);
      }
      this._selectionIdsBeforeLastSingleEditClick = null;
      this.selectOnly([...next], { redraw: true });
      return;
    }
    this._lastShiftPointerDownMergeUuid = null;
    this._selectionIdsBeforeLastSingleEditClick = [...this._selectionIds];
    /**
     * Without full repaint, Konva nodes keep stroke/hitRect from previous selection (they do not turn red).
     * Exception: first click (`detail === 1`) on vertex with `doubleClickRoomCm` must preserve the same instance
     * of node for second tap / `detail >= 2` (see tests and `vertexDoubleClick`).
     */
    const preserveNodeForVertexContinueTap = floorPlanPreserveSelectionRedrawForVertexContinueTap(
      detail,
      this.roomBackgroundPointerMode,
      el,
    );
    this.selectOnly([el.uuid], { redraw: !preserveNodeForVertexContinueTap });
  }

  /**
   * Same intent as {@link _applyEditModePointerDownSelection} when Konva does not attach `evt.evt`
   * (some touch gestures / wrappers): at least set selection so later `dragstart` works.
   */
  private _applyEditModePointerDownSelectionNoDom(el: UppFloorPlanElement): void {
    if (!this.edit) {
      return;
    }
    const vertexContinueDraw =
      el instanceof UppFloorPlanVertex &&
      this.roomBackgroundPointerMode === 'doubleClickRoomCm';
    if (vertexContinueDraw) {
      return;
    }
    this._clearVertexContinueDrawAccum();
    if (this._selectionIds.size > 1 && this._selectionIds.has(el.uuid)) {
      return;
    }
    if (this._selectionIds.size === 1 && this._selectionIds.has(el.uuid)) {
      return;
    }
    this._selectionIdsBeforeLastSingleEditClick = [...this._selectionIds];
    this.selectOnly([el.uuid], { redraw: false });
  }

  /**
   * In edit mode: on **press** (before `dragstart`) aligns selection with element under pointer without repaint
   * completo, para poder **mousedown + arrastre** en un solo gesto (el `click`/`tap` solo llega en mouseup).
   */
  private _applyEditModePointerDownSelection(el: UppFloorPlanElement, domEv: MouseEvent | PointerEvent): void {
    if (!this.edit) {
      return;
    }
    const vertexContinueDraw =
      el instanceof UppFloorPlanVertex &&
      this.roomBackgroundPointerMode === 'doubleClickRoomCm';
    if (vertexContinueDraw) {
      const consumed = this._maybeEmitVertexContinueDrawFromVertexClick(el as UppFloorPlanVertex, domEv);
      if (consumed) {
        return;
      }
    } else {
      this._clearVertexContinueDrawAccum();
    }
    const detail = floorPlanEditModeClickDetail(domEv);
    if (detail >= 2 && this._selectionIdsBeforeLastSingleEditClick !== null) {
      this._applyEditModeClickSelection(el, domEv);
      return;
    }
    const shiftKey = (domEv as MouseEvent).shiftKey === true;
    if (shiftKey && !vertexContinueDraw) {
      if (this._selectionIds.size > 1 && this._selectionIds.has(el.uuid)) {
        return;
      }
      if (this._selectionIds.size === 1 && this._selectionIds.has(el.uuid)) {
        return;
      }
      if (!this._selectionIds.has(el.uuid)) {
        this._selectionIdsBeforeLastSingleEditClick = null;
        this.selectOnly([...this._selectionIds, el.uuid], { redraw: false });
        this._lastShiftPointerDownMergeUuid = el.uuid;
        return;
      }
    }
    if (this._selectionIds.size > 1 && this._selectionIds.has(el.uuid)) {
      return;
    }
    if (this._selectionIds.size === 1 && this._selectionIds.has(el.uuid)) {
      return;
    }
    this._lastShiftPointerDownMergeUuid = null;
    this._selectionIdsBeforeLastSingleEditClick = [...this._selectionIds];
    this.selectOnly([el.uuid], { redraw: false });
  }

  /**
   * Destruye **todas** las instancias `Konva.Transformer` colgando del layer (no solo `this._transformer`).
   * Avoids orphan selection frames if internal reference and canvas get out of sync.
   */
  private _destroyKonvaTransformersOnLayer(): void {
    const layer = this._layer;
    if (layer == null || typeof layer.getChildren !== 'function') {
      this._transformer = null;
      return;
    }
    const kids = layer.getChildren().slice() as unknown[];
    for (const ch of kids) {
      const node = ch as { getClassName?: () => string; destroy?: () => void };
      if (
        typeof node.getClassName === 'function' &&
        node.getClassName() === 'Transformer' &&
        typeof node.destroy === 'function'
      ) {
        node.destroy();
      }
    }
    this._transformer = null;
  }

  private _computeViewTransformForModel(model: UppFloorPlanModel): UppFloorPlanViewTransform {
    const host = this._elementRef.nativeElement;
    const prefs = mergeUppFloorPlanPreferences(this.preferences);
    const theme = mergeUppFloorPlanTheme(this.theme ?? undefined, UPPKONVA_LIGHTTHEME);
    const dimPad = estimateRoomDimensionLabelOverflowPx(theme.roomDimensionFontSize);
    return computeUppFloorPlanViewTransform({
      roomWidthCm: model.room.widthCm,
      roomHeightCm: model.room.heightCm,
      frameWidthPx: Math.max(1, host.clientWidth),
      frameHeightPx: Math.max(1, host.clientHeight),
      marginPx: prefs.marginPx,
      dimensionInsetRightPx: dimPad.right,
      dimensionInsetBottomPx: dimPad.bottom,
      autoOptimizeOrientation: this._orientationLockUpright ? false : prefs.autoOptimizeOrientation,
      viewOrientationDeg: this._orientationLockUpright ? 0 : prefs.viewOrientationDeg,
      viewZoom: prefs.viewZoom * this._userZoomFactor,
    });
  }

  private _attachEditTransformerIfNeeded(
    K: any,
    paintSequence: readonly UppFloorPlanElement[],
    scalePxPerCm: number,
  ): void {
    if (!this.edit || this._selectionIds.size === 0 || !K.Transformer) {
      return;
    }
    const selectedEls = paintSequence.filter((e) => this._selectionIds.has(e.uuid));
    const transformable = selectedEls.filter((e) => isFloorPlanElementTransformable(e));
    if (transformable.length === 0) {
      return;
    }
    const targets: any[] = [];
    for (const el of transformable) {
      const target = this._findContentNodeByElementUuid(el.uuid);
      if (!target) {
        return;
      }
      target.draggable(true);
      targets.push(target);
    }
    if (targets.length === 0) {
      return;
    }

    const minPx = Math.max(12, scalePxPerCm * 4);

    this._transformer = new K.Transformer({
      nodes: targets,
      rotateEnabled: true,
      rotationSnaps: [0, 90, 180, 270],
      rotationSnapTolerance: 4,
      anchorStroke: '#b91c1c',
      anchorFill: '#fee2e2',
      anchorSize: 10,
      borderStroke: '#dc2626',
      borderDash: [6, 4],
      borderStrokeWidth: 2,
      rotateAnchorOffset: 40,
      padding: 4,
      boundBoxFunc: floorPlanTransformerBoundBoxWithMinSize(minPx),
    });

    const commitTransform = () => {
      /** Prevents subsequent `click`/`tap` on same pointerup from clearing selection (Transformer already destroyed). */
      this._suppressNextBackgroundClick = true;
      const multiUniform = transformable.length > 1 && this.selectionIsUniformKind();
      const anchor = floorPlanResolveTransformEndAnchor({
        multiUniform,
        gestureAnchor: this._uniformGroupTransformGestureAnchor,
        transformer: this._transformer,
      });
      const groupRotationGesture = multiUniform && this._uniformGroupGestureUsedRotater;
      const rotationSnapshot = floorPlanTransformCommitRotationSnapshot(
        multiUniform,
        this._uniformGroupTransformModelRotationSnapshot,
        transformable.length,
      );
      if (multiUniform) {
        uniformSyncMultiUniformKindGroupTransform(
          targets,
          transformable,
          scalePxPerCm,
          rotationSnapshot,
          anchor,
          groupRotationGesture,
        );
        for (const el of transformable) {
          this._emitGeometryCommit(el);
        }
      } else {
        for (let i = 0; i < targets.length; i++) {
          const tnode = targets[i];
          const el = transformable[i];
          if (!tnode || !el) {
            continue;
          }
          syncKonvaNodeGeometryToElement(tnode, el, scalePxPerCm, 'resizeOrRotate');
          this._emitGeometryCommit(el);
        }
      }
      this._uniformGroupTransformModelRotationSnapshot = null;
      this._uniformGroupTransformGestureAnchor = null;
      this._uniformGroupGestureUsedRotater = false;
      this._ngZone.runOutsideAngular(() => this._redrawScene());
    };

    this._transformer.on('transformstart', () => {
      this._uniformGroupGestureUsedRotater = false;
      if (transformable.length > 1 && this.selectionIsUniformKind()) {
        this._uniformGroupTransformModelRotationSnapshot = transformable.map((el) => el.rotationDeg);
        this._uniformGroupTransformGestureAnchor =
          this._transformer != null && typeof this._transformer.getActiveAnchor === 'function'
            ? this._transformer.getActiveAnchor()
            : null;
      } else {
        this._uniformGroupTransformModelRotationSnapshot = null;
        this._uniformGroupTransformGestureAnchor = null;
      }
    });
    this._transformer.on('transform', () => {
      if (transformable.length > 1 && this.selectionIsUniformKind()) {
        const a =
          this._transformer != null && typeof this._transformer.getActiveAnchor === 'function'
            ? this._transformer.getActiveAnchor()
            : null;
        if (a === 'rotater') {
          this._uniformGroupGestureUsedRotater = true;
        }
      }
    });
    this._transformer.on('transformend', commitTransform);

    this._layer.add(this._transformer);
    this._transformer.moveToTop?.();
    /**
     * El `dragmove` inyectado por `_proxyDrag` del Transformer puede interferir con el arrastre del nodo
     * (e.g. first gesture after selecting). Frame follows node via `absoluteTransformChange` -> `update()`.
     * Same idea as uniform multi-selection (avoid double delta on first `dragmove`).
     */
    const tr = this._transformer as { _getEventNamespace?: () => string };
    const ns = floorPlanTransformerEventNamespace(tr);
    if (ns) {
      for (const t of targets) {
        t.off(`dragmove.${ns}`);
      }
    }
    if (typeof this._transformer.forceUpdate === 'function') {
      this._transformer.forceUpdate();
    }
    this._layer?.batchDraw?.();
  }

  private async _loadKonvaAndCreateStage(): Promise<void> {
    const Konva = await loadKonvaCore();
    if (this._destroyed) {
      return;
    }
    this._konva = Konva;
    const frame = this._frameRef.nativeElement;
    const host = this._elementRef.nativeElement;
    const w = Math.max(1, host.clientWidth);
    const h = Math.max(1, host.clientHeight);
    this._stage = new Konva.Stage({
      container: frame,
      width: w,
      height: h,
    });
    this._layer = new Konva.Layer();
    this._contentGroup = new Konva.Group({ listening: true });
    this._layer.add(this._contentGroup);
    this._marqueeOverlay = new Konva.Group({ listening: false, id: 'upp-floor-plan-marquee-overlay' });
    this._layer.add(this._marqueeOverlay);
    this._stage.add(this._layer);
    this._installEditModeMarqueeSelection();
    this._installStageDeselectOnBackgroundClick();
    this._installRoomBackgroundDoubleClick();
    this._installRoomBackgroundSingleClickForDrawMode();
    this._installPerimeterDrawPreviewPointerHandlers();
    this._konvaReady = true;
    this._installResizeObserver();
    this._installViewNavigation();
    this._redrawScene();
  }

  /** Click/tap on stage, layer, or empty content (no listening element): clear selection. */
  private _installStageDeselectOnBackgroundClick(): void {
    const handler = (e: { target?: unknown }) => {
      if (this._destroyed || !this._stage || !this._layer || !this._contentGroup) {
        return;
      }
      if (this._suppressNextBackgroundClick) {
        this._suppressNextBackgroundClick = false;
        return;
      }
      const target = e.target as any;
      if (!target || konvaNodeIsUnderTransformer(target)) {
        return;
      }
      const isBackground =
        target === this._stage || target === this._layer || target === this._contentGroup;
      if (!isBackground) {
        return;
      }
      this._clearVertexContinueDrawAccum();
      if (this._selectionIds.size === 0) {
        return;
      }
      this.clearSelection();
    };
    this._stage.on('click tap', handler);
  }

  /** Double-click on empty room → room cm (for hosts adding perimeter vertices). */
  private _installRoomBackgroundDoubleClick(): void {
    const handler = (e: { evt?: MouseEvent | PointerEvent; target?: unknown }) => {
      if (this._destroyed || !this._stage || !this._layer || !this._contentGroup) {
        return;
      }
      if (this._roomBackgroundSingleClickTimer) {
        clearTimeout(this._roomBackgroundSingleClickTimer);
        this._roomBackgroundSingleClickTimer = null;
      }
      if (this.roomBackgroundPointerMode !== 'doubleClickRoomCm' || !this.edit) {
        return;
      }
      const target = e.target as any;
      if (!target || konvaNodeIsUnderTransformer(target)) {
        return;
      }
      const isBackground =
        target === this._stage || target === this._layer || target === this._contentGroup;
      if (!isBackground) {
        return;
      }
      const domEv = e.evt;
      if (!domEv) {
        return;
      }
      const pt = this.clientEventToRoomCm(domEv);
      if (!pt) {
        return;
      }
      this._ngZone.run(() => {
        this.roomBackgroundDoubleClick.emit({ xCm: pt.xCm, yCm: pt.yCm, domEvent: domEv });
      });
    };
    this._stage.on('dblclick dbltap', handler);
  }

  /**
   * Debounced single click on empty room background (same target rules as double-click).
   * Cancels when a double-click is recognized so the first click of a double-click does not close a polygon.
   */
  private _installRoomBackgroundSingleClickForDrawMode(): void {
    const handler = (e: { evt?: MouseEvent | PointerEvent; target?: unknown }) => {
      if (this._destroyed || !this._stage || !this._layer || !this._contentGroup) {
        return;
      }
      if (this.roomBackgroundPointerMode !== 'doubleClickRoomCm' || !this.edit) {
        return;
      }
      const target = e.target as any;
      if (!target || konvaNodeIsUnderTransformer(target)) {
        return;
      }
      const isBackground =
        target === this._stage || target === this._layer || target === this._contentGroup;
      if (!isBackground) {
        return;
      }
      const domEv = e.evt;
      if (!domEv) {
        return;
      }
      const detail =
        typeof (domEv as MouseEvent).detail === 'number' ? (domEv as MouseEvent).detail : 1;
      if (this._roomBackgroundSingleClickTimer) {
        clearTimeout(this._roomBackgroundSingleClickTimer);
        this._roomBackgroundSingleClickTimer = null;
      }
      if (detail > 1) {
        return;
      }
      this._roomBackgroundSingleClickTimer = setTimeout(() => {
        this._roomBackgroundSingleClickTimer = null;
        const model = this.model;
        const hasOpenChain =
          !!model &&
          model.elements.some(
            (e) => e instanceof UppFloorPlanVertex && !e.closed,
          );
        if (!hasOpenChain) {
          return;
        }
        const pt = this.clientEventToRoomCm(domEv);
        if (!pt) {
          return;
        }
        this._ngZone.run(() => {
          this.roomBackgroundSingleClick.emit({ xCm: pt.xCm, yCm: pt.yCm, domEvent: domEv });
        });
      }, 450);
    };
    this._stage.on('click tap', handler);
  }

  /**
   * Dashed segment from the last perimeter vertex (max `sort`) to the pointer while
   * {@link roomBackgroundPointerMode} is `doubleClickRoomCm` and {@link UppFloorPlanStageComponent.edit} is on.
   * Single-click still does not add vertices; only double-click does.
   */
  private _installPerimeterDrawPreviewPointerHandlers(): void {
    if (!this._stage) {
      return;
    }
    this._perimeterPreviewPointerCleanup?.();
    const container = this._stage.container() as HTMLElement;
    const onMove = (ev: PointerEvent): void => {
      if (this._destroyed) {
        return;
      }
      if (!this.edit || this.roomBackgroundPointerMode !== 'doubleClickRoomCm') {
        this._clearPerimeterPreviewPointerTarget();
        return;
      }
      const model = this.model;
      if (!model || !resolveFloorPlanPerimeterPreviewSourceVertex(model, this.vertexPreviewAnchorId)) {
        this._clearPerimeterPreviewPointerTarget();
        return;
      }
      const pt = this.clientEventToRoomCm(ev);
      if (!pt || !Number.isFinite(pt.xCm) || !Number.isFinite(pt.yCm)) {
        return;
      }
      this._perimeterPreviewEndCm = pt;
      this._schedulePerimeterPreviewRefresh();
    };
    const onLeave = (): void => {
      if (this._destroyed) {
        return;
      }
      this._clearPerimeterPreviewPointerTarget();
    };
    container.addEventListener('pointermove', onMove, { passive: true });
    container.addEventListener('pointerleave', onLeave);
    this._perimeterPreviewPointerCleanup = (): void => {
      container.removeEventListener('pointermove', onMove);
      container.removeEventListener('pointerleave', onLeave);
    };
  }

  private _clearPerimeterPreviewPointerTarget(): void {
    if (this._perimeterPreviewEndCm === null) {
      return;
    }
    this._perimeterPreviewEndCm = null;
    this._schedulePerimeterPreviewRefresh();
  }

  private _schedulePerimeterPreviewRefresh(): void {
    if (this._perimeterPreviewRafId !== 0 || this._destroyed) {
      return;
    }
    this._perimeterPreviewRafId = requestAnimationFrame(() => {
      this._perimeterPreviewRafId = 0;
      if (this._destroyed || !this._konvaReady || !this._stage || !this._contentGroup || !this._konva) {
        return;
      }
      const model = this.model;
      const K = this._konva;
      if (!model) {
        return;
      }
      const t = this._computeViewTransformForModel(model);
      const theme = mergeUppFloorPlanTheme(this.theme ?? {}, UPPKONVA_LIGHTTHEME);
      this._syncPerimeterPreviewLine(K, t.scalePxPerCm, theme);
      this._stage.batchDraw();
    });
  }

  private _perimeterPreviewShouldDraw(): boolean {
    return floorPlanPerimeterPreviewShouldDraw({
      edit: this.edit,
      roomBackgroundPointerMode: this.roomBackgroundPointerMode,
      previewEndCm: this._perimeterPreviewEndCm,
      model: this.model,
      anchorVertexId: this.vertexPreviewAnchorId,
    });
  }

  private _syncPerimeterPreviewLine(K: any, scalePxPerCm: number, theme: UppFloorPlanTheme): void {
    if (!this._contentGroup) {
      return;
    }
    const existing = this._contentGroup.findOne('#upp-perimeter-preview');
    existing?.destroy();
    if (!this._perimeterPreviewShouldDraw()) {
      return;
    }
    const model = this.model;
    if (!model) {
      return;
    }
    const last = resolveFloorPlanPerimeterPreviewSourceVertex(model, this.vertexPreviewAnchorId);
    const end = this._perimeterPreviewEndCm;
    if (!last || !end || !K.Line) {
      return;
    }
    const s = scalePxPerCm;
    const lc = floorPlanVertexCenterCm(last);
    const line = new K.Line({
      id: 'upp-perimeter-preview',
      points: [lc.xCm * s, lc.yCm * s, end.xCm * s, end.yCm * s],
      stroke: theme.elementStroke,
      dash: [6, 5],
      strokeWidth: Math.max(2, floorPlanInteriorWallStrokeWidthPx(theme) * 0.85),
      opacity: 0.92,
      listening: false,
    });
    this._contentGroup.add(line);
  }

  private _cancelViewClickPulse(): void {
    if (this._viewClickPulseCancel) {
      this._viewClickPulseCancel();
      this._viewClickPulseCancel = null;
    }
  }

  private _cancelAllAttentionPulses(): void {
    for (const c of this._attentionPulseCancelById.values()) {
      try {
        c();
      } catch {
        /* ignore */
      }
    }
    this._attentionPulseCancelById.clear();
  }

  /**
   * View mode: start a looping scale pulse on each table with `attention` (same timing as click pulse).
   */
  private _syncAttentionPulseLoops(): void {
    this._cancelAllAttentionPulses();
    if (
      this._destroyed ||
      !this._contentGroup ||
      !this._konva ||
      this.edit ||
      this.viewClickPulseDurationMs <= 0
    ) {
      return;
    }
    const model = this.model;
    if (!model) {
      return;
    }
    for (const el of model.elements) {
      if (!(el instanceof UppFloorPlanTable) || !el.attention || !el.attentionAnimated) {
        continue;
      }
      const target = this._contentGroup.findOne(
        (n: any) => n !== undefined && typeof n.id === 'function' && n.id() === el.uuid,
      );
      if (!target) {
        continue;
      }
      const cancel = runFloorPlanViewAttentionPulseLoop(this._konva, target, {
        durationMs: this.viewClickPulseDurationMs,
        peakScale: this.viewClickPulsePeakScale,
        valleyScale: this.viewClickPulseValleyScale,
      });
      this._attentionPulseCancelById.set(el.uuid, cancel);
    }
  }

  private _cancelMarqueeDocListeners(): void {
    if (this._marqueePointerCleanup) {
      this._marqueePointerCleanup();
    }
  }

  private _clearMarqueeVisual(): void {
    try {
      this._marqueeRect?.destroy();
    } catch {
      /* ignore */
    }
    this._marqueeRect = null;
  }

  private _installEditModeMarqueeSelection(): void {
    if (!this._stage) {
      return;
    }
    const onDown = (e: { target?: unknown; evt?: MouseEvent | PointerEvent | TouchEvent }) => {
      if (this._destroyed || !this.edit || !this.active) {
        return;
      }
      if (!this._stage || !this._layer || !this._contentGroup || !this._marqueeOverlay) {
        return;
      }
      const dom = e.evt;
      if (dom && 'button' in dom && (dom as MouseEvent).button !== 0) {
        return;
      }
      if (this._roomBackgroundSingleClickTimer) {
        clearTimeout(this._roomBackgroundSingleClickTimer);
        this._roomBackgroundSingleClickTimer = null;
      }
      if (dom) {
        this._stage.setPointersPositions(dom);
      }
      const pos = this._stage.getPointerPosition();
      if (!pos) {
        return;
      }
      /**
       * No fiarse solo de `e.target`: a veces Konva entrega el `Group` de contenido como target aunque el
       * pointer is over a table/shape; then dragging started marquee instead of drag.
       * English comment normalized.
       */
      const hit =
        typeof this._stage.getIntersection === 'function'
          ? (this._stage.getIntersection(pos) as any)
          : null;
      const domTarget = e.target as any;
      if (konvaNodeIsUnderTransformer(domTarget) || (hit != null && konvaNodeIsUnderTransformer(hit))) {
        return;
      }
      const onEmptyMarqueeSurface =
        hit == null ||
        hit === this._stage ||
        hit === this._layer ||
        hit === this._contentGroup;
      if (!onEmptyMarqueeSurface) {
        return;
      }
      this._cancelMarqueeDocListeners();
      this._marqueeCandidateStart = { x: pos.x, y: pos.y };
      this._marqueeCandidatePointerId =
        dom && 'pointerId' in dom && typeof (dom as PointerEvent).pointerId === 'number'
          ? (dom as PointerEvent).pointerId
          : null;

      const doc = (this._stage.container() as HTMLElement | undefined)?.ownerDocument ?? globalThis.document;
      const onMove = (ev: PointerEvent): void => {
        if (this._marqueeCandidateStart === null || !this._stage) {
          return;
        }
        if (this._marqueeCandidatePointerId !== null && ev.pointerId !== this._marqueeCandidatePointerId) {
          return;
        }
        this._stage.setPointersPositions(ev);
        const cur = this._stage.getPointerPosition();
        if (!cur) {
          return;
        }
        const start = this._marqueeCandidateStart;
        const dx = cur.x - start.x;
        const dy = cur.y - start.y;
        if (!this._marqueeRect && floorPlanMarqueeDragExceedsStartThreshold(dx, dy)) {
          this._ensureMarqueeRect(start);
        }
        if (this._marqueeRect) {
          this._layoutMarqueeRectFromCorners(start, cur);
          this._stage.batchDraw();
        }
      };
      const onUp = (ev: PointerEvent): void => {
        if (this._marqueeCandidatePointerId !== null && ev.pointerId !== this._marqueeCandidatePointerId) {
          return;
        }
        if (this._stage) {
          this._stage.setPointersPositions(ev);
        }
        const end = this._stage?.getPointerPosition() ?? null;
        this._finishMarqueePointerInteraction(end);
        this._cancelMarqueeDocListeners();
      };
      doc.addEventListener('pointermove', onMove, { passive: true });
      doc.addEventListener('pointerup', onUp, { passive: true });
      doc.addEventListener('pointercancel', onUp, { passive: true });
      this._marqueePointerCleanup = (): void => {
        doc.removeEventListener('pointermove', onMove);
        doc.removeEventListener('pointerup', onUp);
        doc.removeEventListener('pointercancel', onUp);
        this._marqueePointerCleanup = null;
      };
    };
    this._stage.on('mousedown touchstart', onDown);
  }

  private _ensureMarqueeRect(start: { x: number; y: number }): void {
    if (!this._konva || !this._marqueeOverlay) {
      return;
    }
    const K = this._konva;
    this._marqueeRect = new K.Rect({
      x: start.x,
      y: start.y,
      width: 0,
      height: 0,
      fill: 'rgba(37, 99, 235, 0.12)',
      stroke: '#2563eb',
      strokeWidth: 1,
      listening: false,
      name: 'upp-floor-plan-marquee',
    });
    this._marqueeOverlay.add(this._marqueeRect);
  }

  private _layoutMarqueeRectFromCorners(a: { x: number; y: number }, b: { x: number; y: number }): void {
    if (!this._marqueeRect) {
      return;
    }
    const box = floorPlanMarqueeBoxFromOpposingCorners(a, b);
    this._marqueeRect.setAttrs({ x: box.x, y: box.y, width: box.width, height: box.height });
  }

  private _finishMarqueePointerInteraction(endPos: { x: number; y: number } | null): void {
    const start = this._marqueeCandidateStart;
    this._marqueeCandidateStart = null;
    this._marqueeCandidatePointerId = null;
    if (!start) {
      return;
    }
    if (!this._marqueeRect) {
      return;
    }
    if (!endPos || !this._stage) {
      this._clearMarqueeVisual();
      this._stage?.batchDraw();
      return;
    }
    const box = floorPlanMarqueeBoxFromOpposingCorners(start, endPos);
    this._clearMarqueeVisual();
    this._stage.batchDraw();
    if (floorPlanMarqueeBoxTooSmall(box)) {
      return;
    }
    const ids =
      this.model && this._contentGroup && this._stage
        ? collectFloorPlanElementIdsIntersectingStageBox(
            this.model,
            this._contentGroup,
            this._stage,
            box,
          )
        : [];
    this._suppressNextBackgroundClick = true;
    this.selectOnly(ids);
    this._selectionIdsBeforeLastSingleEditClick = null;
  }

  private _installResizeObserver(): void {
    if (this._destroyed) {
      return;
    }
    this._resizeObserver = new ResizeObserver(() => {
      this._syncSizeAndRedraw();
    });
    this._resizeObserver.observe(this._elementRef.nativeElement);
  }

  /** Fit transform plus live pan offsets (for hit-testing and focal zoom). */
  private _viewTransformWithPan(model: UppFloorPlanModel): UppFloorPlanViewTransform {
    const t = this._computeViewTransformForModel(model);
    return {
      ...t,
      originXPx: t.originXPx + this._panOffsetXPx,
      originYPx: t.originYPx + this._panOffsetYPx,
    };
  }

  private _emitViewTransformChange(): void {
    const payload: UppKonvaTransform = {
      zoomFactor: this._userZoomFactor,
      panOffsetXPx: this._panOffsetXPx,
      panOffsetYPx: this._panOffsetYPx,
    };
    this._ngZone.run(() => this.viewTransformChange.emit(payload));
  }

  /**
   * Sets user zoom; when `focalStagePx` is set, adjusts pan so that stage point stays over the same room cm.
   * When the change zooms **out toward** {@link FLOOR_PLAN_USER_ZOOM_MIN} (100%, fit-to-frame), pan instead
   * "comes home" proportionally to the remaining zoom excess (see {@link _homePanTowardMinZoom}) so the room
   * never lands at 100% still panned off-center — that takes priority over focal-point preservation for the step.
   */
  private _applyUserZoom(
    nextZoom: number,
    focalStagePx: { x: number; y: number } | null,
  ): void {
    const model = this.model;
    const clamped = clampFloorPlanUserZoomFactor(nextZoom);
    if (!model || Math.abs(clamped - this._userZoomFactor) < 1e-9) {
      return;
    }
    if (this._homePanTowardMinZoom(clamped)) {
      this._userZoomFactor = clamped;
    } else if (focalStagePx) {
      const before = this._viewTransformWithPan(model);
      this._userZoomFactor = clamped;
      const afterBase = this._computeViewTransformForModel(model);
      const after: UppFloorPlanViewTransform = {
        ...afterBase,
        originXPx: afterBase.originXPx + this._panOffsetXPx,
        originYPx: afterBase.originYPx + this._panOffsetYPx,
      };
      const delta = panDeltaToKeepFocalStagePoint(
        focalStagePx,
        (x, y) => stagePxToCmPoint(before, x, y),
        (xCm, yCm) => cmPointToStagePx(after, xCm, yCm),
      );
      this._panOffsetXPx += delta.dx;
      this._panOffsetYPx += delta.dy;
    } else {
      this._userZoomFactor = clamped;
    }
    this._scheduleUserZoomRedraw();
    this._emitViewTransformChange();
  }

  /**
   * A pinch gesture or a burst of wheel ticks can call {@link _applyUserZoom} many times per animation frame
   * (one per raw `pointermove`/`wheel` event); rebuilding the whole scene (`destroyChildren()` + recreate every
   * node) on each one is wasted work once several updates land in the same frame. Keeps only the latest zoom/pan
   * state and performs a single {@link _redrawScene} per frame; {@link _emitViewTransformChange} stays
   * synchronous in the caller since it only pushes numbers to `viewTransformChange`, not a canvas rebuild.
   * Discrete zoom actions ({@link zoomIn}, {@link zoomOut}, {@link fitRoomToFrame}) keep their own immediate redraw.
   */
  private _scheduleUserZoomRedraw(): void {
    if (this._userZoomRedrawRafId !== 0 || this._destroyed) {
      return;
    }
    this._ngZone.runOutsideAngular(() => {
      this._userZoomRedrawRafId = requestAnimationFrame(() => {
        this._userZoomRedrawRafId = 0;
        if (this._destroyed) {
          return;
        }
        this._redrawScene();
      });
    });
  }

  /**
   * Scales the accumulated pan offset down by the ratio of remaining zoom excess (`clampedZoom - MIN`) over
   * the current excess, so it reaches exactly `(0, 0)` when `clampedZoom` hits {@link FLOOR_PLAN_USER_ZOOM_MIN}.
   * No-op (returns `false`) unless `clampedZoom` is strictly closer to the min than the current zoom.
   */
  private _homePanTowardMinZoom(clampedZoom: number): boolean {
    const prevExcess = this._userZoomFactor - FLOOR_PLAN_USER_ZOOM_MIN;
    const nextExcess = clampedZoom - FLOOR_PLAN_USER_ZOOM_MIN;
    if (nextExcess >= prevExcess - 1e-9 || prevExcess <= 1e-9) {
      return false;
    }
    const homeScale = Math.max(0, nextExcess / prevExcess);
    this._panOffsetXPx *= homeScale;
    this._panOffsetYPx *= homeScale;
    return true;
  }

  private _panBy(dx: number, dy: number): void {
    if (!Number.isFinite(dx) || !Number.isFinite(dy) || (dx === 0 && dy === 0)) {
      return;
    }
    this._panOffsetXPx += dx;
    this._panOffsetYPx += dy;
    if (this._contentGroup) {
      const model = this.model;
      if (model) {
        const t = this._computeViewTransformForModel(model);
        this._contentGroup.x(t.originXPx + this._panOffsetXPx);
        this._contentGroup.y(t.originYPx + this._panOffsetYPx);
        this._stage?.batchDraw();
      } else {
        this._ngZone.runOutsideAngular(() => this._redrawScene());
      }
    } else {
      this._ngZone.runOutsideAngular(() => this._redrawScene());
    }
    this._emitViewTransformChange();
  }

  private _teardownViewNavigation(): void {
    this._viewNavCleanup?.();
    this._viewNavCleanup = null;
  }

  /**
   * View-mode only (`!edit`): wheel zoom, pinch zoom, and one-finger pan (background **or** over an element,
   * since view mode has no element dragging — a plain tap still selects via Konva's own `click`/`tap`, see
   * {@link PAN_THRESHOLD_PX} and the click-suppression guard on {@link UppFloorPlanStageComponent._suppressNextBackgroundClick}).
   * One-finger pan only engages **above** the min zoom (100%); at 100% the pointer is left untouched so a
   * host's floor-swipe gesture can claim it (see `PAN_MIN_ZOOM` below).
   */
  private _installViewNavigation(): void {
    this._teardownViewNavigation();
    const host = this._elementRef.nativeElement;
    if (!host) {
      return;
    }

    const pointers = new Map<number, { x: number; y: number }>();
    /**
     * Konva binds its own `pointermove`/`pointerup` to its **`stage.content`** div (a descendant of `host`).
     * Pointer capture retargets subsequent events to the *capturing* element's own ancestor chain only —
     * capturing on `host` (an ancestor of `content`) would silently stop Konva's own listeners from ever
     * firing again for that pointer, killing its `click`/`tap` detection. Capture on `ev.target` instead
     * (the actual hit element, a descendant of both `content` and `host`) so both keep receiving the bubbled
     * events normally.
     */
    const capturedTargets = new Map<number, Element>();
    let pinchStartDist = 0;
    let pinchStartZoom = 1;
    let panActive = false;
    let panLastX = 0;
    let panLastY = 0;
    let panCandidate = false;
    const PAN_THRESHOLD_PX = 4;
    /**
     * At the min zoom (100%, fit-to-frame) the room already fits entirely — one-finger drag hands off to the
     * host's floor-swipe gesture (see `SWIPE_ZOOM_BLOCK_ABOVE` in `upp-tab-tables.ts`). Above 100%
     * pan takes over. Pinch-zoom (two fingers) is never gated by this.
     */
    const PAN_MIN_ZOOM = FLOOR_PLAN_USER_ZOOM_MIN + 1e-6;

    const hostPoint = (ev: PointerEvent | WheelEvent): { x: number; y: number } => {
      const rect = host.getBoundingClientRect();
      return { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
    };

    const capturePointer = (ev: PointerEvent): void => {
      const target = ev.target;
      if (!(target instanceof Element) || typeof target.setPointerCapture !== 'function') {
        return;
      }
      try {
        target.setPointerCapture(ev.pointerId);
        capturedTargets.set(ev.pointerId, target);
      } catch {
        /* ignore */
      }
    };

    const releaseCapturedPointer = (pointerId: number): void => {
      const target = capturedTargets.get(pointerId);
      capturedTargets.delete(pointerId);
      if (!target) {
        return;
      }
      try {
        target.releasePointerCapture(pointerId);
      } catch {
        /* ignore */
      }
    };

    const onWheel = (ev: WheelEvent) => {
      if (this._destroyed || this.edit || !this.active) {
        return;
      }
      ev.preventDefault();
      const mult = wheelDeltaToZoomMultiplier(ev.deltaY);
      if (mult === 1) {
        return;
      }
      this._applyUserZoom(this._userZoomFactor * mult, hostPoint(ev));
    };

    const onPointerDown = (ev: PointerEvent) => {
      if (this._destroyed || this.edit || !this.active) {
        return;
      }
      pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      if (pointers.size === 2) {
        panActive = false;
        panCandidate = false;
        const pts = [...pointers.values()];
        const a = pts[0];
        const b = pts[1];
        if (!a || !b) {
          return;
        }
        pinchStartDist = Math.hypot(b.x - a.x, b.y - a.y) || 1;
        pinchStartZoom = this._userZoomFactor;
        capturePointer(ev);
        return;
      }
      if (pointers.size === 1) {
        if (this._userZoomFactor <= PAN_MIN_ZOOM) {
          /** At 100% leave the pointer alone so the floor-swipe gesture can claim it. */
          panCandidate = false;
          return;
        }
        /**
         * No `isBackgroundTarget` gate: view mode never drags elements, so a single-finger drag that starts
         * over a table/bar still pans (dense floors leave little empty background to grab). A quick tap
         * (movement under {@link PAN_THRESHOLD_PX}) never sets `panActive`, so Konva's own `click`/`tap` still
         * selects normally.
         */
        panCandidate = true;
        panActive = false;
        panLastX = ev.clientX;
        panLastY = ev.clientY;
        capturePointer(ev);
      }
    };

    const onPointerMove = (ev: PointerEvent) => {
      if (this._destroyed || this.edit || !this.active) {
        return;
      }
      if (!pointers.has(ev.pointerId)) {
        return;
      }
      pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      if (pointers.size === 2) {
        if (ev.cancelable) {
          ev.preventDefault();
        }
        const pts = [...pointers.values()];
        const a = pts[0];
        const b = pts[1];
        if (!a || !b) {
          return;
        }
        const dist = Math.hypot(b.x - a.x, b.y - a.y) || 1;
        const mid = {
          x: (a.x + b.x) / 2,
          y: (a.y + b.y) / 2,
        };
        const rect = host.getBoundingClientRect();
        const focal = { x: mid.x - rect.left, y: mid.y - rect.top };
        const next = pinchStartZoom * (dist / pinchStartDist);
        this._applyUserZoom(next, focal);
        return;
      }
      if (pointers.size === 1 && (panCandidate || panActive)) {
        const dx = ev.clientX - panLastX;
        const dy = ev.clientY - panLastY;
        if (!panActive) {
          if (Math.hypot(dx, dy) < PAN_THRESHOLD_PX) {
            return;
          }
          panActive = true;
          this._suppressNextBackgroundClick = true;
        }
        if (ev.cancelable) {
          ev.preventDefault();
        }
        panLastX = ev.clientX;
        panLastY = ev.clientY;
        this._panBy(dx, dy);
      }
    };

    const onPointerUp = (ev: PointerEvent) => {
      pointers.delete(ev.pointerId);
      releaseCapturedPointer(ev.pointerId);
      if (pointers.size < 2) {
        pinchStartDist = 0;
      }
      if (pointers.size === 0) {
        panActive = false;
        panCandidate = false;
      }
    };

    host.addEventListener('wheel', onWheel, { passive: false });
    host.addEventListener('pointerdown', onPointerDown);
    host.addEventListener('pointermove', onPointerMove);
    host.addEventListener('pointerup', onPointerUp);
    host.addEventListener('pointercancel', onPointerUp);

    this._viewNavCleanup = () => {
      host.removeEventListener('wheel', onWheel);
      host.removeEventListener('pointerdown', onPointerDown);
      host.removeEventListener('pointermove', onPointerMove);
      host.removeEventListener('pointerup', onPointerUp);
      host.removeEventListener('pointercancel', onPointerUp);
    };
  }

  private _syncSizeAndRedraw(): void {
    if (!this._stage) {
      return;
    }
    const host = this._elementRef.nativeElement;
    syncStageSizeToHost(this._stage, host);
    const prefs = mergeUppFloorPlanPreferences(this.preferences);
    if (this._layer) {
      applyLayerPixelRatioCap(this._layer, prefs.pixelRatioMax, globalThis.window?.devicePixelRatio ?? 1);
    }
    if (this.edit && this._liveEditDragDepth > 0) {
      this._stage.batchDraw();
      return;
    }
    this._redrawScene();
  }

  private _redrawScene(modelOverride?: UppFloorPlanModel | null): void {
    if (!this._stage || !this._layer || !this._contentGroup) {
      return;
    }
    const host = this._elementRef.nativeElement;
    const prefs = mergeUppFloorPlanPreferences(this.preferences);
    syncStageSizeToHost(this._stage, host);
    applyLayerPixelRatioCap(this._layer, prefs.pixelRatioMax, globalThis.window?.devicePixelRatio ?? 1);

    const theme = resolveUppFloorPlanThemeLabelFontSize(
      mergeUppFloorPlanTheme(this.theme ?? {}, UPPKONVA_LIGHTTHEME),
      prefs.labelSize,
    );
    const K = this._konva;
    if (!K?.Rect || !K?.Line || !K?.Text || !K?.Group || !K?.Circle) {
      return;
    }

    this._redrawSceneDepth += 1;
    try {
    this._sceneRedrawGeneration++;
    this._destroyKonvaTransformersOnLayer();

    this._cancelViewClickPulse();
    this._cancelAllAttentionPulses();
    this._cancelMarqueeDocListeners();
    this._clearMarqueeVisual();
    this._marqueeCandidateStart = null;
    this._marqueeCandidatePointerId = null;
    this._contentGroup.destroyChildren();

    const model = modelOverride ?? this.model;
    if (!model) {
      this._stage.batchDraw();
      return;
    }

    const t = this._computeViewTransformForModel(model);

    this._contentGroup.setAttrs({
      x: t.originXPx + this._panOffsetXPx,
      y: t.originYPx + this._panOffsetYPx,
      rotation: t.screenRotationDeg,
    });

    const s = t.scalePxPerCm;
    const perimeterVertices = model.elements.filter(
      (e): e is UppFloorPlanVertex => e instanceof UppFloorPlanVertex,
    );

    const rwPx = t.roomWidthCm * s;
    const rhPx = t.roomHeightCm * s;
    const dash =
      Array.isArray(theme.roomPerimeterDash) && theme.roomPerimeterDash.length > 0
        ? theme.roomPerimeterDash
        : undefined;
    const roomRect = new K.Rect({
      x: 0,
      y: 0,
      width: rwPx,
      height: rhPx,
      fillEnabled: false,
      stroke: theme.roomStroke,
      strokeWidth: theme.roomStrokeWidth,
      ...(dash ? { dash } : {}),
      listening: false,
      id: 'upp-floor-plan-room-perimeter',
    });
    this._contentGroup.add(roomRect);

    const stageBox = computeFloorPlanRoomRectStageAabb(t, rwPx, rhPx);
    const boxWpx = stageBox.maxX - stageBox.minX;
    const boxHpx = stageBox.maxY - stageBox.minY;
    const extentHorizontalCm = boxWpx / s;
    const extentVerticalCm = boxHpx / s;
    const dimCounter = new K.Group({
      x: 0,
      y: 0,
      rotation: -t.screenRotationDeg,
      listening: false,
    });
    const dimGeo = createUppFloorPlanRoomDimensionGroup(
      K,
      theme,
      boxWpx,
      boxHpx,
      extentHorizontalCm,
      extentVerticalCm,
    );
    dimGeo.setAttrs({
      x: stageBox.minX - t.originXPx,
      y: stageBox.minY - t.originYPx,
      listening: false,
    });
    dimCounter.add(dimGeo);
    this._contentGroup.add(dimCounter);

    const wallGroups = groupPerimeterVerticesByPolygonId(perimeterVertices);
    const wallPolygonIds = sortedPolygonIds(wallGroups);
    const fillColor =
      theme.interiorClosedPolygonFill ?? UPPKONVA_LIGHTTHEME.interiorClosedPolygonFill;
    for (const pid of wallPolygonIds) {
      const chain = wallGroups.get(pid);
      if (!chain || chain.length < 2) {
        continue;
      }
      const first = chain[0];
      const closed = !!first && first.closed === true && chain.length >= 3;
      const pts = floorPlanPerimeterChainPointsPx(chain, s);
      const interiorWalls = new K.Line({
        id: `upp-floor-plan-interior-wall-${pid}`,
        points: pts,
        closed,
        fill: closed ? fillColor : undefined,
        fillEnabled: closed,
        stroke: theme.elementStroke,
        strokeWidth: floorPlanInteriorWallStrokeWidthPx(theme),
        lineCap: 'round',
        lineJoin: 'round',
        listening: false,
      });
      this._contentGroup.add(interiorWalls);
    }

    const listen = this.active || this.edit;
    const paintSequence = buildFloorPlanPaintSequence(model.elements);

    for (const el of paintSequence) {
      const selected = this._selectionIds.has(el.uuid);
      const node = createFloorPlanElementNode(K, el, s, theme, selected, listen, this.edit);
      if (listen) {
        node.on('click tap', (evt: { evt: MouseEvent | PointerEvent; target?: unknown }) => {
          /**
           * Konva fires `click`/`tap` on pointerup over the same node regardless of movement in between
           * (no built-in threshold unless the node is `draggable`). View mode allows panning to start over
           * an element (see {@link _installViewNavigation}), so a pan that ends on the same table would
           * otherwise reselect it; consume the shared suppression flag set when the pan gesture engaged.
           */
          if (this._suppressNextBackgroundClick) {
            this._suppressNextBackgroundClick = false;
            return;
          }
          const domEv = evt.evt;
          const konvaTarget = evt.target;
          const vertexContinueDraw =
            el instanceof UppFloorPlanVertex &&
            this.edit &&
            this.roomBackgroundPointerMode === 'doubleClickRoomCm';
          if (this.edit && listen && domEv) {
            if (vertexContinueDraw) {
              const consumedContinueDraw = this._maybeEmitVertexContinueDrawFromVertexClick(
                el as UppFloorPlanVertex,
                domEv,
              );
              if (!consumedContinueDraw) {
                this._applyEditModeClickSelection(el, domEv);
              }
            } else {
              this._clearVertexContinueDrawAccum();
              this._applyEditModeClickSelection(el, domEv);
            }
          } else {
            if (vertexContinueDraw && domEv) {
              this._maybeEmitVertexContinueDrawFromVertexClick(
                el as UppFloorPlanVertex,
                domEv,
              );
            } else {
              this._clearVertexContinueDrawAccum();
            }
            /** View mode: click must update selection (e.g. host inspector); edit mode already does this via {@link _applyEditModeClickSelection}. */
            if (!this.edit && listen) {
              this.selectOnly([el.uuid]);
            }
          }
          this._ngZone.run(() => {
            const ref = toUppKonvaSceneElementRef(el);
            const clickPayload: UppFloorPlanElementClickPayload =
              el instanceof UppFloorPlanBar
                ? {
                    element: ref,
                    domEvent: domEv,
                    custom: el.custom,
                    stool: floorPlanBarStoolIndexFromPointerTarget(el, konvaTarget),
                  }
                : { element: ref, domEvent: domEv, custom: el.custom };
            this.elementClick.emit(clickPayload);
            for (const h of this._elementClickHandlers) {
              h(ref, domEv);
            }
          });
          /**
           * Start the pulse after host handlers (e.g. `selectOnly` → `_redrawScene`) so the Konva node
           * is not destroyed immediately — otherwise the tween is cancelled and only selection styling changes.
           * View click feedback applies only to **table** and **bar** shapes (not furniture, architecture, vertices).
           */
          if (
            !this.edit &&
            this.viewClickPulseDurationMs > 0 &&
            (el instanceof UppFloorPlanTable || el instanceof UppFloorPlanBar)
          ) {
            const elementId = el.uuid;
            queueMicrotask(() => {
              if (this._destroyed || !this._contentGroup) {
                return;
              }
              const target = this._contentGroup.findOne(
                (n: any) => n !== undefined && typeof n.id === 'function' && n.id() === elementId,
              );
              if (!target) {
                return;
              }
              this._cancelViewClickPulse();
              this._cancelAllAttentionPulses();
              this._viewClickPulseCancel = runFloorPlanViewClickPulse(this._konva, target, {
                durationMs: this.viewClickPulseDurationMs,
                peakScale: this.viewClickPulsePeakScale,
                valleyScale: this.viewClickPulseValleyScale,
                onComplete: () => {
                  this._viewClickPulseCancel = null;
                  this._syncAttentionPulseLoops();
                },
              });
            });
          }
        });
      }
      const canDragInEdit =
        listen &&
        this.edit &&
        (isFloorPlanElementTransformable(el) || el instanceof UppFloorPlanVertex);
      if (canDragInEdit) {
        node.draggable(true);
        if (el instanceof UppFloorPlanVertex && typeof node.dragDistance === 'function') {
          /** Lets double-click register before Konva treats pointer movement as a drag (default distance is 0). */
          node.dragDistance(10);
        } else if (typeof node.dragDistance === 'function') {
          /** Lower threshold than global (~3px): selection in `pointerdown` + drag in same gesture. */
          node.dragDistance(1);
        }
        const onEditPressStart = (evt: { evt?: MouseEvent | PointerEvent }) => {
          if (!listen) {
            return;
          }
          const domEv = evt.evt;
          if (domEv) {
            this._applyEditModePointerDownSelection(el, domEv);
          } else {
            this._applyEditModePointerDownSelectionNoDom(el);
          }
        };
        /** `pointerdown` covers modern mouse/touch; `mousedown`/`touchstart` for compatibility. The second event in same gesture exits early as "already selected". */
        node.on('pointerdown', onEditPressStart);
        node.on('mousedown touchstart', onEditPressStart);
        node.on('dragstart', () => {
          this._liveEditDragDepth++;
          if (el instanceof UppFloorPlanVertex) {
            this._clearVertexContinueDrawAccum();
          }
          if (!this._selectionIds.has(el.uuid)) {
            this._selectionIdsBeforeLastSingleEditClick = null;
            this.selectOnly([el.uuid], { redraw: false });
          }
          const multiEligible = (id: string): boolean => {
            const e = this._getLiveElementById(id);
            return (
              !!e &&
              (isFloorPlanElementTransformable(e) || e instanceof UppFloorPlanVertex)
            );
          };
          const multi =
            this._selectionIds.size > 1 &&
            this._selectionIds.has(el.uuid) &&
            [...this._selectionIds].every(multiEligible);
          if (multi) {
            this._multiDragStartParentPositions = new Map<string, { x: number; y: number }>();
            this._multiDragStartModelCm = new Map<string, { xCm: number; yCm: number }>();
            for (const id of this._selectionIds) {
              const n = this._contentGroup.findOne(
                (nn: any) => nn !== undefined && typeof nn.id === 'function' && nn.id() === id,
              );
              if (n && typeof n.x === 'function' && typeof n.y === 'function') {
                this._multiDragStartParentPositions.set(id, { x: n.x(), y: n.y() });
              }
              const row = this._getLiveElementById(id);
              if (row) {
                this._multiDragStartModelCm.set(id, { xCm: row.xCm, yCm: row.yCm });
              }
            }
            this._multiDragPrimaryId = el.uuid;
            this._multiDragPrimaryParentStart = { x: node.x(), y: node.y() };
            return;
          }
          if (this._selectionIds.size === 1 && this._selectionIds.has(el.uuid)) {
            return;
          }
          this._selectionIdsBeforeLastSingleEditClick = null;
          this.selectOnly([el.uuid], { redraw: false });
        });
        node.on('dragmove', () => {
          if (
            !this._multiDragStartParentPositions ||
            this._multiDragPrimaryId !== el.uuid ||
            !this._multiDragPrimaryParentStart
          ) {
            return;
          }
          const dx = node.x() - this._multiDragPrimaryParentStart.x;
          const dy = node.y() - this._multiDragPrimaryParentStart.y;
          for (const [id, pos] of this._multiDragStartParentPositions) {
            if (id === el.uuid) {
              continue;
            }
            const n = this._contentGroup.findOne(
              (nn: any) => nn !== undefined && typeof nn.id === 'function' && nn.id() === id,
            );
            if (n && typeof n.x === 'function' && typeof n.y === 'function') {
              n.x(pos.x + dx);
              n.y(pos.y + dy);
            }
          }
        });
        const onDragEnd = () => {
          try {
            if (
              this._multiDragStartParentPositions &&
              this._multiDragStartModelCm &&
              this._multiDragPrimaryId === el.uuid &&
              this._multiDragPrimaryParentStart
            ) {
              const dxPx = node.x() - this._multiDragPrimaryParentStart.x;
              const dyPx = node.y() - this._multiDragPrimaryParentStart.y;
              const dxCm = dxPx / s;
              const dyCm = dyPx / s;
              for (const id of this._selectionIds) {
                const element = this._getLiveElementById(id);
                const startCm = this._multiDragStartModelCm.get(id);
                if (
                  !element ||
                  !startCm ||
                  (!isFloorPlanElementTransformable(element) &&
                    !(element instanceof UppFloorPlanVertex))
                ) {
                  continue;
                }
                element.xCm = roundFloorPlanGeometryCm(startCm.xCm + dxCm);
                element.yCm = roundFloorPlanGeometryCm(startCm.yCm + dyCm);
                this._emitGeometryCommit(element);
              }
              this._multiDragStartParentPositions = null;
              this._multiDragStartModelCm = null;
              this._multiDragPrimaryId = null;
              this._multiDragPrimaryParentStart = null;
              this._ngZone.runOutsideAngular(() => this._redrawScene());
              return;
            }
            syncKonvaNodeGeometryToElement(node, el, s, 'move');
            /** Same as {@link _attachEditTransformerIfNeeded} `transformend`: emit integration before `_redrawScene` to avoid orphan Transformer and first-drag blocking. */
            this._emitGeometryCommit(el);
            this._ngZone.runOutsideAngular(() => this._redrawScene());
          } finally {
            this._liveEditDragDepth = Math.max(0, this._liveEditDragDepth - 1);
          }
        };
        node.on('dragend', onDragEnd);
      }
      this._contentGroup.add(node);
    }

    this._attachEditTransformerIfNeeded(K, paintSequence, s);

    this._syncPerimeterPreviewLine(K, s, theme);

    /**
     * Element labels: badge **above** shapes in paint order (after preview segment).
     * **Bottom-left** of the badge = **screen** top-right of the reference rect (stage coords, Y down), then **−30 %** width on screen X and **+30 %** height on screen Y (overlap inward). Inset is **viewport-axes** (not along the element’s local edges), so it holds for any element rotation and plan `screenRotationDeg`.
     * Tables with chairs: expanded rect per {@link floorPlanElementLabelInsetBBoxCm}.
     * Upright text: `rotation: -screenRotationDeg` + `offsetY: boxH`.
     */
    const labelFontFamily = 'sans-serif';
    const labelFontStyle = 'bold';
    const labelPadX = 8;
    const labelPadY = 6;
    const labelLineGap = 2;
    const labelBadgeCornerRadius = 4;
    /** Inset of badge anchor from bbox top-right: fraction of element width (left) and height (down). */
    const labelBadgeInsetXFrac = 0.3;
    const labelBadgeInsetYFrac = 0.3;
    const measureLabelTextWidthPx = (text: string, fontSize: number): number => {
      const t = new K.Text({
        text,
        fontSize,
        fontFamily: labelFontFamily,
        fontStyle: labelFontStyle,
        listening: false,
      });
      const w = typeof t.width === 'function' ? t.width() : 0;
      t.destroy();
      return w;
    };

    const absContent = this._contentGroup.getAbsoluteTransform();
    const absContentInv = absContent.copy();
    absContentInv.invert();

    for (const el of paintSequence) {
      const layout = floorPlanElementLabelBadgeBoxLayout({
        primary: el.label,
        secondary: el.sublabel,
        baseFontSize: theme.labelFontSize,
        measureTextWidth: measureLabelTextWidthPx,
      });
      if (!layout) {
        continue;
      }
      const { lines, boxW, boxH } = layout;

      const insetBbox = floorPlanElementLabelInsetBBoxCm(el);
      const widthPx = insetBbox.widthCm * s;
      const heightPx = insetBbox.heightCm * s;

      let trStage: { x: number; y: number };
      if (el instanceof UppFloorPlanVertex) {
        const cornersContent = floorPlanVertexLabelRectCornersContentPx(el, s);
        const cornersStage = cornersContent.map((c) => absContent.point(c));
        trStage = pickStageTopRightCornerAmongVertices(cornersStage);
      } else {
        const node = this._contentGroup.findOne(
          (n: any) => n !== undefined && typeof n.id === 'function' && n.id() === el.uuid,
        );
        if (node && typeof node.getAbsoluteTransform === 'function') {
          const absNode = node.getAbsoluteTransform();
          const localCorners = floorPlanElementLabelRectCornersLocalPx(el, s);
          const cornersStage = localCorners.map((c) => absNode.point(c));
          trStage = pickStageTopRightCornerAmongVertices(cornersStage);
        } else {
          const rightPx = insetBbox.rightCm * s;
          const topPx = insetBbox.topCm * s;
          const leftPx = rightPx - widthPx;
          const bottomPx = topPx + heightPx;
          trStage = pickStageTopRightCornerAmongVertices([
            absContent.point({ x: leftPx, y: topPx }),
            absContent.point({ x: rightPx, y: topPx }),
            absContent.point({ x: rightPx, y: bottomPx }),
            absContent.point({ x: leftPx, y: bottomPx }),
          ]);
        }
      }

      const anchorStage = {
        x: trStage.x - labelBadgeInsetXFrac * widthPx,
        y: trStage.y + labelBadgeInsetYFrac * heightPx,
      };
      const anchorContent = absContentInv.point(anchorStage);

      const g = new K.Group({
        id: `upp-floor-plan-el-label-${el.uuid}`,
        x: anchorContent.x,
        y: anchorContent.y,
        offsetX: 0,
        offsetY: boxH,
        rotation: -t.screenRotationDeg,
        listening: false,
      });
      g.add(
        new K.Rect({
          x: 0,
          y: 0,
          width: boxW,
          height: boxH,
          fill: theme.elementLabelBadgeFill,
          stroke: theme.elementLabelBadgeStroke,
          strokeWidth: theme.elementLabelBadgeStrokeWidth,
          cornerRadius: labelBadgeCornerRadius,
          listening: false,
        }),
      );
      let y = labelPadY;
      for (const ln of lines) {
        g.add(
          new K.Text({
            x: labelPadX,
            y,
            text: ln.text,
            fontSize: ln.size,
            fontFamily: labelFontFamily,
            fontStyle: labelFontStyle,
            fill: theme.labelColor,
            listening: false,
          }),
        );
        y += ln.size + labelLineGap;
      }
      this._contentGroup.add(g);
    }

    this._stage.batchDraw();
    queueMicrotask(() => this._syncAttentionPulseLoops());
    } finally {
      this._redrawSceneDepth -= 1;
    }
  }
}
